import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
// Removed unused VolumeUp import
import { useNavigate, useLocation } from 'react-router-dom';
import { getImageUrl, getApiUrl } from '../config';
import useScreenRecorder from '../hooks/useScreenRecorder';
import { useLanguageContext } from '../contexts/LanguageContext';
import SEO, { getWorshipServiceData } from '../components/SEO';

interface WorshipData {
  prayer: string;
  bibleVerse: string;
  bibleReference: string;
  sermon: string;
  sermonSentences: string[];
  analysis?: {
    theme: string;
    category: string;
    emotion: string;
    keywords: string[];
  };
  sermonStyle?: {
    name: string;
    personality: string;
    approach: string;
    tone: string;
  };
  sermonPastor?: {
    name: string;
    locale: string;
    speciality: string;
    primary_situation: string;
  };
  hymn1: {
    title: string;
    thumbnail: string;
    videoUrl: string;
    channel: string;
    duration: string;
  };
  hymn2: {
    title: string;
    thumbnail: string;
    videoUrl: string;
    channel: string;
    duration: string;
  };
  blessing: {
    content: string;
    biblical_basis: string;
    personal_application: string;
    blessing_verse?: string;
    blessing_reference?: string;
    pastor?: {
      name: string;
      locale: string;
      speciality: string;
      primary_situation: string;
    };
    style?: {
      name: string;
      personality: string;
      focus: string;
      tone: string;
    };
  };
  blessingReference: string;
}

// 워십 단계 정의
const WORSHIP_STEPS = {
  LOADING: -1,
  INDEX: 0,
  SCRIPTURE: 1,
  HYMN1: 2,
  SERMON: 3,
  HYMN2: 4,
  BLESSING: 5,
  CHURCH_INVITATION: 6,
  DONATION: 7
} as const;

// YouTube URL을 iframe 임베드 URL로 변환하는 유틸리티 함수
const convertToEmbedUrl = (videoUrl: string): string => {
  if (!videoUrl) return '';
  
  console.log('🔗 Converting video URL:', videoUrl);
  
  // 이미 embed URL인 경우
  if (videoUrl.includes('/embed/')) {
    console.log('✅ Already embed URL:', videoUrl);
    return videoUrl;
  }
  
  // youtube.com/watch?v= 형식
  if (videoUrl.includes('youtube.com/watch?v=')) {
    const videoId = videoUrl.split('watch?v=')[1].split('&')[0];
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;
    console.log('🔄 Converted watch URL to embed:', embedUrl);
    return embedUrl;
  }
  
  // youtu.be/ 형식
  if (videoUrl.includes('youtu.be/')) {
    const videoId = videoUrl.split('youtu.be/')[1].split('?')[0];
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;
    console.log('🔄 Converted youtu.be URL to embed:', embedUrl);
    return embedUrl;
  }
  
  console.log('⚠️ Unknown video URL format:', videoUrl);
  return videoUrl;
};

// 문장 분리 후 다음 조각 앞에 붙은 닫는 따옴표를 이전 문장 끝으로 옮기는 유틸리티 함수
// 닫는 따옴표 판별: 둥근 닫는 따옴표(” ’)이거나, 직선 따옴표 뒤에 공백이 오거나 따옴표만 남은 경우
const mergeClosingQuotes = (fragments: string[]): string[] => {
  const merged: string[] = [];
  for (const fragment of fragments) {
    const closingMatch = fragment.match(/^([”’]+|['"″′‴]+(?=\s|$))\s*/);
    if (!closingMatch || merged.length === 0) {
      merged.push(fragment);
      continue;
    }
    merged[merged.length - 1] += closingMatch[1];
    const rest = fragment.slice(closingMatch[0].length).trim();
    if (rest.length > 0) merged.push(rest);
  }
  return merged;
};

// 성경 구절에서 중복 따옴표 제거하는 유틸리티 함수
const cleanVerseQuotes = (verse: string): string => {
  if (!verse) return '';
  
  // 앞뒤 공백 제거
  let cleaned = verse.trim();
  
  // 이미 따옴표로 감싸져 있다면 제거
  if ((cleaned.startsWith('"') && cleaned.endsWith('"')) || 
      (cleaned.startsWith('"') && cleaned.endsWith('"')) ||
      (cleaned.startsWith('「') && cleaned.endsWith('」'))) {
    cleaned = cleaned.slice(1, -1);
  }
  
  return cleaned;
};

// 영어 성경 본문 끝에 잘못 이어붙은 'Pastor 이름' 꼬리를 제거
const cleanEnglishPastorSuffix = (text: string): string => {
  if (!text) return '';
  let result = text.trim();
  // 패턴 예: "..." - Pastor John Doe / — Pastor John / (Pastor John)
  result = result.replace(/\s*[-–—]\s*Pastor\s+[A-Za-z .'-]+$/i, '');
  result = result.replace(/\s*\(?Pastor\s+[A-Za-z .'-]+\)?\s*$/i, '');
  return result;
};

// 백엔드 CrewAI 진행 단계(SSE progress stage) → 로딩 모달 단계 인덱스
const GENERATION_STAGE_INDEX: Record<string, number> = {
  started: 0,
  analysis: 0,
  scripture_hymn: 1,
  sermon: 2,
  blessing: 3,
  finalizing: 4
};

interface WorshipStreamResult {
  status: number;
  data: any;
}

// ai/simple-worship-stream 의 SSE를 읽어 progress는 콜백으로 전달하고 result 이벤트를 반환
const requestWorshipWithProgress = async (
  requestBody: Record<string, unknown>,
  onStage: (stage: string) => void
): Promise<WorshipStreamResult> => {
  const response = await fetch(getApiUrl('ai/simple-worship-stream'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream'
    },
    body: JSON.stringify(requestBody)
  });
  if (!response.ok || !response.body) throw new Error(`Worship stream failed: ${response.status}`);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const frames = buffer.split('\n\n');
    buffer = frames.pop() || '';

    for (const frame of frames) {
      const lines = frame.split('\n');
      const eventLine = lines.find(line => line.startsWith('event:'));
      const dataText = lines
        .filter(line => line.startsWith('data:'))
        .map(line => line.slice(5).trim())
        .join('\n');
      if (!eventLine || !dataText) continue;

      const eventName = eventLine.slice(6).trim();
      const payload = JSON.parse(dataText);
      if (eventName === 'progress') {
        onStage(payload.stage);
        continue;
      }
      if (eventName === 'result') return payload as WorshipStreamResult;
      if (eventName === 'error') throw new Error(payload.message || 'Worship stream error');
    }
  }

  throw new Error('Worship stream ended without result');
};

const WorshipPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguageContext();
  // 초기 진입 시 리뷰 페이지에서 교회 권유 단계로 점프 요청이 있는지 확인
  const initialJumpToChurch = Boolean(location.state?.jumpToChurchInvitation);
  const [currentStep, setCurrentStep] = useState<number>(() => initialJumpToChurch ? WORSHIP_STEPS.CHURCH_INVITATION : WORSHIP_STEPS.LOADING);
  const [isProcessing, setIsProcessing] = useState<boolean>(() => initialJumpToChurch ? false : true);

  // 관리자 세션 분석용: 성경말씀/설교/축도 단계 도달을 단계별 1회만 기록 (실패해도 예배 진행에는 영향 없음)
  const reportedWorshipStepsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const stepNames: Record<number, string> = {
      [WORSHIP_STEPS.SCRIPTURE]: 'scripture',
      [WORSHIP_STEPS.SERMON]: 'sermon',
      [WORSHIP_STEPS.BLESSING]: 'blessing'
    };
    const step = stepNames[currentStep];
    if (!step || reportedWorshipStepsRef.current.has(step)) return;
    reportedWorshipStepsRef.current.add(step);
    fetch(getApiUrl('worship/progress'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step })
    }).catch(() => undefined);
  }, [currentStep]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  // Removed unused state variables: isPlayingAudio, showInstructions
  const [firstHymnPlaying, setFirstHymnPlaying] = useState(false);
  const [secondHymnPlaying, setSecondHymnPlaying] = useState(false);
  const [firstHymnShowingPlaceholder, setFirstHymnShowingPlaceholder] = useState(false);
  const [secondHymnShowingPlaceholder, setSecondHymnShowingPlaceholder] = useState(false);
  const [firstHymnThumbnailFading, setFirstHymnThumbnailFading] = useState(false);
  const [secondHymnThumbnailFading, setSecondHymnThumbnailFading] = useState(false);
  // iOS에서 사용자 제스처로 시작한 경우 음소거 해제 허용
  const [firstHymnUserInitiated, setFirstHymnUserInitiated] = useState(false);
  const [secondHymnUserInitiated, setSecondHymnUserInitiated] = useState(false);
  const [showIndexMessage, setShowIndexMessage] = useState(true); // 초기부터 표시하여 깜빡임 방지
  const [showBlessingMessage, setShowBlessingMessage] = useState(false);
  const [showFullSermon, setShowFullSermon] = useState(false);
  const [sermonCompleted, setSermonCompleted] = useState(false);
  const [blessingAnimationComplete, setBlessingAnimationComplete] = useState(false);
  const [blessingVerseComplete, setBlessingVerseComplete] = useState(false); // 성경 구절 표시 완료 추적
  const [showBlessingReference, setShowBlessingReference] = useState(false); // 참조 구절 표시 상태
  const [showBlessingVerseText, setShowBlessingVerseText] = useState(false); // 본문 구절 표시 상태
  // Removed unused blessingAnimationDuration state
  const [blessingPhase, setBlessingPhase] = useState<'pause' | 'lineByLine' | 'scrollUp' | 'complete'>('pause');
  const [visibleBlessingLines, setVisibleBlessingLines] = useState(0);
  const [blessingLines, setBlessingLines] = useState<string[]>([]);
  const [maxLinesBeforeScroll, setMaxLinesBeforeScroll] = useState(8);
  // scrollOffset 제거됨 - 사용하지 않음
  const [showChurchInvitationMessage, setShowChurchInvitationMessage] = useState<boolean>(() => initialJumpToChurch ? true : false);
  // 페이지 전환 관련 상태 제거 - 깜빡임 방지를 위해
  const [isMobile, setIsMobile] = useState(false);
  const [showBlessingStopModal, setShowBlessingStopModal] = useState(false);
  // iOS 감지 (iPadOS 포함)
  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isClassicIOS = /iPad|iPhone|iPod/i.test(ua);
    const isIPadOS = ua.includes('Mac') && typeof (window as any).ontouchend !== 'undefined';
    return isClassicIOS || isIPadOS;
  }, []);
  const initializationRef = useRef(false);
  const apiCallInProgressRef = useRef(false);
  const sermonTimerRef = useRef<NodeJS.Timeout | null>(null);
  const endCardShownRef = useRef<boolean>(false);
  const recordingStartedRef = useRef<boolean>(false);
  const jumpHandledRef = useRef<boolean>(false);
  const [showEndCard, setShowEndCard] = useState<boolean>(false);
  const [showCreateVideoPrompt, setShowCreateVideoPrompt] = useState<boolean>(false);
  const [showRecordConfirmModal, setShowRecordConfirmModal] = useState<boolean>(false);
  const [isRecordingMode, setIsRecordingMode] = useState<boolean>(false);
  const blessingContainerRef = useRef<HTMLDivElement | null>(null);
  const { isRecording, start: startRecording, stop: stopRecording } = useScreenRecorder();
  const [isGeneratingServerVideo, setIsGeneratingServerVideo] = useState<boolean>(false);
  const [worshipType, setWorshipType] = useState<string>('personal');
  const [issueTitle, setIssueTitle] = useState<string>('');
  const [worshipData, setWorshipData] = useState<WorshipData>({
    prayer: '',
    bibleVerse: '',
    bibleReference: '',
    sermon: '',
    sermonSentences: [],
    sermonStyle: undefined,
    sermonPastor: undefined,
    hymn1: {
      title: '주는 은혜와 사랑이',
      thumbnail: 'https://i.ytimg.com/vi/Mn6CDjp_3LY/mqdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/Mn6CDjp_3LY',
      channel: t('worship.hymn.defaultChannel'),
      duration: '3:45'
    },
    hymn2: {
      title: '나의 기도하는 그 시간',
      thumbnail: 'https://i.ytimg.com/vi/YwAquUTgNkQ/mqdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/YwAquUTgNkQ',
      channel: t('worship.hymn.defaultChannel'),
      duration: '4:12'
    },
    blessing: {
      content: '여호와는 네게 복을 주시고 너를 지키시기를 원하며\n여호와는 그의 얼굴을 네게 비치사 은혜 베푸시기를 원하며\n여호와는 그 얼굴을 네게로 향하여 드사 평강 주시기를 원하노라\n아멘',
      biblical_basis: '민수기 6:24-26의 아론의 축복',
      personal_application: '하나님의 복과 은혜가 개인에게 임함',
      blessing_verse: '여호와는 네게 복을 주시고 너를 지키시기를 원하며 여호와는 그의 얼굴을 네게 비치사 은혜 베푸시기를 원하며 여호와는 그 얼굴을 네게로 향하여 드사 평강 주시기를 원하노라',
      blessing_reference: '민수기 6:24-26',
      pastor: undefined,
      style: undefined
    },
    blessingReference: '민수기 6:24-26'
  });

  const processingSteps = useMemo(() => [
    t('worship.processing.scripture'),
    t('worship.processing.hymn'),
    t('worship.processing.sermon'),
    t('worship.processing.meditation')
  ], [t]);
  const generationSteps = useMemo(() => [
    t('worship.processing.analysis'),
    t('worship.processing.scriptureHymn'),
    t('worship.processing.sermon'),
    t('worship.processing.blessing'),
    t('worship.processing.finalizing')
  ], [t]);
  const [currentProcessingStep, setCurrentProcessingStep] = useState(0);
  const [hasLiveProgress, setHasLiveProgress] = useState(false);
  const loadingSteps = hasLiveProgress ? generationSteps : processingSteps;

  // 모바일 감지
  useEffect(() => {
    const checkIsMobile = () => {
      const userAgent = navigator.userAgent;
      const mobileKeywords = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;
      
      return mobileKeywords.test(userAgent) || isTouchDevice || isSmallScreen;
    };

    setIsMobile(checkIsMobile());

    const handleResize = () => {
      setIsMobile(checkIsMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 키보드 이벤트 핸들러
  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    console.log('🔑 Key pressed:', event.key, 'Current step:', currentStep);
    
    // 키보드 기본 동작 방지 (스크롤 등)
    event.preventDefault();
    // 스크롤 위치 고정
    window.scrollTo(0, 0);
    
    // 축도 단계에서는 아무 키나 누르면 중단 확인 모달 표시
    if (currentStep === WORSHIP_STEPS.BLESSING) {
      setShowBlessingStopModal(true);
      return;
    }
    
    if (currentStep === WORSHIP_STEPS.LOADING) {
      console.log('⏳ Loading, ignoring key press');
      return;
    }
    
    // 설교 단계에서 문장별 진행
    if (currentStep === WORSHIP_STEPS.SERMON) {
      // 모두 보기 모드에서는 키 입력 무시
      if (showFullSermon) {
        console.log('📖 In fullSermon mode, ignoring key press');
        return;
      }
      
      // 설교 완료 상태에서는 다음 단계로 이동
      if (sermonCompleted) {
        console.log('📖 Sermon completed, moving to next step');
        // 여기서 return하지 않고 아래 다음 단계 로직으로 진행
      } else {
        // 설교 진행 중
        if (currentSentenceIndex < worshipData.sermonSentences.length - 1) {
          // 자동 타이머 취소하고 다음 문장으로 이동
          if (sermonTimerRef.current) {
            clearTimeout(sermonTimerRef.current);
          }
          console.log('📖 Moving to next sentence:', currentSentenceIndex + 1);
          setCurrentSentenceIndex(prev => prev + 1);
          return;
        } else if (currentSentenceIndex >= worshipData.sermonSentences.length - 1) {
          setSermonCompleted(true);
          console.log('📖 Sermon completed, ready for next step');
          // Don't return here - let it fall through to allow step advancement
        }
      }
    }

    // 다음 단계로 이동
    if (currentStep < WORSHIP_STEPS.DONATION) {
      console.log('➡️ Moving to next step from', currentStep, 'to', currentStep + 1);
      
      // 즉시 페이지 전환 (깜빡임 방지)
      setCurrentStep(currentStep + 1);
      
      // 설교 단계에서 넘어갈 때 상태 초기화
      if (currentStep === WORSHIP_STEPS.SERMON) {
        setCurrentSentenceIndex(0);
        setSermonCompleted(false);
        setShowFullSermon(false);
      }
    } else {
      console.log('🏁 Already at final step');
    }
  }, [currentStep, currentSentenceIndex, worshipData.sermonSentences.length, showFullSermon, sermonCompleted]);

  // 터치 이벤트 핸들러 (모바일 인터랙션 보존)
  const handleTouchStart = useCallback((event: TouchEvent) => {
    console.log('👆 Touch started:', 'Current step:', currentStep);

    // 모달이 열려 있으면 버튼/내부 요소 동작을 막지 않음
    if (showBlessingStopModal) return;

    // 설교 모두보기 모드에서는 전역 터치 핸들러를 완전히 우회하여 스크롤 가능하게 함
    if (currentStep === WORSHIP_STEPS.SERMON && showFullSermon) {
      return;
    }

    const targetElement = event.target as HTMLElement | null;
    // 축도 본문이 성경구절 위에 잘리지 않도록, 본문 영역 터치는 스크롤로 둔다
    if (targetElement?.closest('[data-blessing-scroll]')) return;

    const isInteractive = !!(
      targetElement &&
      targetElement.closest(
        'button, a, input, textarea, select, [role="button"], .MuiDialog-root, .MuiDialog-container, iframe, video'
      )
    );

    // 축도 단계: 화면 터치는 모달 표시. 단, 인터랙티브 요소 터치는 허용
    if (currentStep === WORSHIP_STEPS.BLESSING) {
      if (isInteractive) return; // 버튼/프롬프트/모달 등은 정상 동작
      event.preventDefault();
      window.scrollTo(0, 0);
      setShowBlessingStopModal(true);
      return;
    }

    // 다른 단계: 인터랙티브 요소(YouTube iframe 포함) 터치는 방해하지 않음
    if (isInteractive) return;

    // 기본 스크롤 방지 및 단계 진행 로직
    event.preventDefault();
    window.scrollTo(0, 0);

    if (currentStep === WORSHIP_STEPS.LOADING) {
      console.log('⏳ Loading, ignoring touch');
      return;
    }

    if (currentStep === WORSHIP_STEPS.SERMON) {
      if (showFullSermon) {
        console.log('📖 In fullSermon mode, ignoring touch');
        return;
      }

      if (sermonCompleted) {
        console.log('📖 Sermon completed, moving to next step');
      } else {
        if (currentSentenceIndex < worshipData.sermonSentences.length - 1) {
          if (sermonTimerRef.current) {
            clearTimeout(sermonTimerRef.current);
          }
          console.log('📖 Moving to next sentence:', currentSentenceIndex + 1);
          setCurrentSentenceIndex(prev => prev + 1);
          return;
        } else if (currentSentenceIndex >= worshipData.sermonSentences.length - 1) {
          setSermonCompleted(true);
          console.log('📖 Sermon completed, ready for next step');
        }
      }
    }

    if (currentStep < WORSHIP_STEPS.DONATION) {
      console.log('➡️ Moving to next step from', currentStep, 'to', currentStep + 1);
      setCurrentStep(currentStep + 1);
      if (currentStep === WORSHIP_STEPS.SERMON) {
        setCurrentSentenceIndex(0);
        setSermonCompleted(false);
        setShowFullSermon(false);
      }
    } else {
      console.log('🏁 Already at final step');
    }
  }, [currentStep, currentSentenceIndex, worshipData.sermonSentences.length, showFullSermon, sermonCompleted, showBlessingStopModal]);

  // 키보드 및 터치 이벤트 리스너 등록 (헌금 페이지는 제외)
  useEffect(() => {
    // 헌금 페이지에서는 키보드/터치 이벤트 비활성화
    if (currentStep === WORSHIP_STEPS.DONATION) {
      return;
    }
    
    window.addEventListener('keydown', handleKeyPress);
    
    if (isMobile) {
      window.addEventListener('touchstart', handleTouchStart, { passive: false });
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      if (isMobile) {
        window.removeEventListener('touchstart', handleTouchStart);
      }
    };
  }, [handleKeyPress, handleTouchStart, isMobile, currentStep]);

  // 페이지 스크롤 방지 (헌금 페이지와 설교 모두보기는 제외)
  useEffect(() => {
    // 헌금 페이지에서는 스크롤 허용
    if (currentStep === WORSHIP_STEPS.DONATION) {
      return;
    }

    // 설교 모두보기 모드에서는 전역 스크롤 차단을 적용하지 않음
    if (currentStep === WORSHIP_STEPS.SERMON && showFullSermon) {
      return;
    }
    
    // 스크롤을 맨 위로 고정
    window.scrollTo(0, 0);
    
    // 스크롤 방지 (축도 본문 영역은 성경구절에 가리지 않게 내부 스크롤 허용)
    const preventScroll = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[data-blessing-scroll]')) return;
      e.preventDefault();
      window.scrollTo(0, 0);
    };

    // 스크롤 이벤트 리스너 추가
    window.addEventListener('scroll', preventScroll, { passive: false });
    window.addEventListener('wheel', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });

    // 컴포넌트 언마운트 시 정리
    return () => {
      window.removeEventListener('scroll', preventScroll);
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventScroll);
    };
  }, [currentStep, showFullSermon]); // 설교 모두보기 전환 시에도 재설정

  // 페이지 전환 효과 제거 - 깜빡임 완전 방지
  // useEffect(() => {
  //   if (isPageTransitioning && nextStep !== null) {
  //     // 즉시 페이지 변경으로 깜빡임 방지
  //     setCurrentStep(nextStep);
  //     setIsPageTransitioning(false);
  //     setNextStep(null);
  //   }
  // }, [isPageTransitioning, nextStep]);

  // 현재 단계 변화 추적
  useEffect(() => {
    console.log('📍 Current step changed to:', currentStep, 'isProcessing:', isProcessing);
    
    // 설교 단계로 들어갈 때 상태 초기화
    if (currentStep === WORSHIP_STEPS.SERMON) {
      setCurrentSentenceIndex(0);
      setSermonCompleted(false);
      setShowFullSermon(false);
      if (sermonTimerRef.current) {
        clearTimeout(sermonTimerRef.current);
      }
    }
    
    // 찬송 타이머를 저장할 변수
    let hymn1Timer1: NodeJS.Timeout | null = null;
    let hymn1Timer2: NodeJS.Timeout | null = null;
    let hymn2Timer1: NodeJS.Timeout | null = null;
    let hymn2Timer2: NodeJS.Timeout | null = null;
    
    // 찬송 단계로 들어갈 때 자동재생 활성화
    if (currentStep === WORSHIP_STEPS.HYMN1) {
      console.log('🎵 HYMN1 단계 시작 - 자동 재생 준비');
      // 사용자가 스페이스바를 눌러서 이 단계에 도달했으므로 사용자 상호작용으로 간주
      setFirstHymnUserInitiated(true);
      // 즉시 placeholder 표시
      setFirstHymnShowingPlaceholder(true);
      setFirstHymnThumbnailFading(false);
      
      // 3초 후에 페이드아웃 시작
      hymn1Timer1 = setTimeout(() => {
        console.log('🎵 HYMN1 페이드아웃 시작');
        setFirstHymnThumbnailFading(true);
        // 1초간 페이드아웃 후 YouTube 동영상 표시
        hymn1Timer2 = setTimeout(() => {
          console.log('🎵 HYMN1 재생 시작 - firstHymnPlaying = true');
          setFirstHymnShowingPlaceholder(false);
          setFirstHymnPlaying(true);
          // 사용자 상호작용 시뮬레이트 (document에 클릭 이벤트 발생)
          document.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        }, 1000);
      }, 3000);
    }
    
    if (currentStep === WORSHIP_STEPS.HYMN2) {
      console.log('🎵 HYMN2 단계 시작 - 자동 재생 준비');
      // 사용자가 스페이스바를 눌러서 이 단계에 도달했으므로 사용자 상호작용으로 간주
      setSecondHymnUserInitiated(true);
      // 즉시 placeholder 표시
      setSecondHymnShowingPlaceholder(true);
      setSecondHymnThumbnailFading(false);
      
      // 3초 후에 페이드아웃 시작
      hymn2Timer1 = setTimeout(() => {
        console.log('🎵 HYMN2 페이드아웃 시작');
        setSecondHymnThumbnailFading(true);
        // 1초간 페이드아웃 후 YouTube 동영상 표시
        hymn2Timer2 = setTimeout(() => {
          console.log('🎵 HYMN2 재생 시작 - secondHymnPlaying = true');
          setSecondHymnShowingPlaceholder(false);
          setSecondHymnPlaying(true);
          // 사용자 상호작용 시뮬레이트 (document에 클릭 이벤트 발생)
          document.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        }, 1000);
      }, 3000);
    }
    
    // 축도 단계로 들어갈 때 상태 초기화 및 애니메이션 시간 계산
    if (currentStep === WORSHIP_STEPS.BLESSING) {
      setBlessingAnimationComplete(false);
      setBlessingVerseComplete(false); // 성경 구절 완료 상태 초기화
      setShowBlessingReference(false); // 참조 구절 상태 초기화
      setShowBlessingVerseText(false); // 본문 구절 상태 초기화
      setBlessingPhase('pause');
      setVisibleBlessingLines(0);
      // scrollOffset 제거됨
      
      // 축도 텍스트를 줄별로 분할
      console.log('📿 축도 초기화 시작');
      console.log('📿 worshipData.blessing:', worshipData.blessing);
      console.log('📿 worshipData.blessing?.content:', worshipData.blessing?.content);
      console.log('📿 worshipData.blessing?.content type:', typeof worshipData.blessing?.content);
      
      let blessingContent = '';
      if (worshipData.blessing?.content && typeof worshipData.blessing.content === 'string') {
        blessingContent = worshipData.blessing.content;
        console.log('📿 Using blessing content from API:', blessingContent);
      } else {
        // 언어별 기본 축복 구절
        const defaultBlessings = {
          ko: '여호와는 네게 복을 주시고 너를 지키시기를 원하며\n여호와는 그의 얼굴을 네게 비치사 은혜 베푸시기를 원하며\n여호와는 그 얼굴을 네게로 향하여 드사 평강 주시기를 원하노라\n아멘',
          en: 'The Lord bless you and keep you;\nThe Lord make his face shine upon you and be gracious to you;\nThe Lord turn his face toward you and give you peace.\nAmen',
          th: 'ขอพระเยโฮวาห์ทรงอวยพรและคุ้มครองท่าน\nขอพระเยโฮวาห์ทรงให้พระพักตร์ของพระองค์ส่องสว่างเหนือท่านและโปรดกรุณาท่าน\nขอพระเยโฮวาห์ทรงแนบพระพักตร์เข้าหาท่านและประทานสันติภาพแก่ท่าน\nอาเมน'
        };
        blessingContent = defaultBlessings[currentLanguage as keyof typeof defaultBlessings] || defaultBlessings.ko;
        console.log('📿 Using default blessing:', blessingContent);
      }
      
      const lines = blessingContent
        .split('\n')
        .filter(line => line.trim().length > 0)
        .flatMap(line => {
          // 일본어, 중국어, 태국어 문자가 포함된 경우 세밀한 분할
          const hasAsianChars = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u0E00-\u0E7F]/.test(line);
          if (currentLanguage === 'ja' || currentLanguage === 'zh' || currentLanguage === 'th' || hasAsianChars) {
            // 1단계: 구두점으로 분할 (。、；：, 및 태국어 구두점)
            const punctuationSplit = line.split(/[。、；：,\u0E2F\u0E46\u0E4F\u0E5A\u0E5B]/).map(segment => segment.trim()).filter(segment => segment.length > 0);
            
            // 2단계: 각 분할된 세그먼트가 너무 긴 경우 추가 분할
            return punctuationSplit.flatMap(segment => {
              // 태국어의 경우 더 짧은 길이로 분할 (태국어는 공백이 없어서 더 짧게 나눠야 함)
              const maxLength = currentLanguage === 'th' ? 20 : 25;
              const forceBreakLength = currentLanguage === 'th' ? 25 : 30;
              
              if (segment.length > maxLength) {
                const chunks: string[] = [];
                let currentChunk = '';
                
                // 문자 단위로 분할
                for (let i = 0; i < segment.length; i++) {
                  currentChunk += segment[i];
                  
                  // 기준 길이 이상이거나 자연스러운 구분점에서 분할
                  if (currentChunk.length >= maxLength) {
                    // 다음 문자가 조사나 어미인지 확인하여 자연스러운 분할점 찾기
                    const nextChars = segment.slice(i + 1, i + 3);
                    let isNaturalBreakPoint = false;
                    
                    if (currentLanguage === 'th') {
                      // 태국어: 모음이나 성조 기호 다음, 또는 특정 문자 다음
                      isNaturalBreakPoint = /^[\u0E30-\u0E3A\u0E40-\u0E4E]/.test(nextChars) || 
                                          /^[ที่และเป็นมี]/.test(nextChars);
                    } else {
                      // 일본어/중국어
                      isNaturalBreakPoint = /^[はがをにでと]/.test(nextChars) || 
                                          /^[的了在是有]/.test(nextChars);
                    }
                    
                    if (isNaturalBreakPoint || currentChunk.length >= forceBreakLength) {
                      chunks.push(currentChunk.trim());
                      currentChunk = '';
                    }
                  }
                }
                if (currentChunk.trim()) chunks.push(currentChunk.trim());
                return chunks.length > 0 ? chunks : [segment];
              }
              return [segment];
            });
          }

          // 한국어/영어: 마침표(.)와 쉼표(,)에서 줄바꿈
          if (currentLanguage === 'ko' || currentLanguage === 'en') {
            // 구두점을 보존하여 세그먼트를 구성 (각 세그먼트가 구두점으로 끝나도록 함)
            const parts = line.split(/([.,])/);
            const punctuationSegments: string[] = [];
            for (let i = 0; i < parts.length; i += 2) {
              const text = (parts[i] || '').trim();
              const delim = parts[i + 1] || '';
              if (text.length > 0 || delim) {
                punctuationSegments.push((text + delim).trim());
              }
            }
            const segments = punctuationSegments.filter(seg => seg.length > 0);

            // 세그먼트가 과도하게 긴 경우 단어 기반 균등 분할 적용
            // (끝에 단어 한두 개만 남지 않도록 각 줄 길이를 비슷하게 맞춤)
            return segments.flatMap(segment => {
              if (segment.length > 40) {
                const words = segment.split(' ');
                const chunkCount = Math.ceil(segment.length / 40);
                const targetLength = Math.ceil(segment.length / chunkCount);
                const chunks: string[] = [];
                let currentChunk = '';
                for (const word of words) {
                  const candidate = currentChunk ? currentChunk + ' ' + word : word;
                  if (candidate.length > targetLength && currentChunk.length > 0 && chunks.length < chunkCount - 1) {
                    chunks.push(currentChunk.trim());
                    currentChunk = word;
                  } else {
                    currentChunk = candidate;
                  }
                }
                if (currentChunk) chunks.push(currentChunk.trim());
                return chunks;
              }
              return [segment];
            });
          }
          
          // 기타 언어는 기존 로직 (긴 줄은 적절한 길이로 분할)
          if (line.length > 40) {
            const words = line.split(' ');
            const chunks: string[] = [];
            let currentChunk = '';
            
            for (const word of words) {
              if ((currentChunk + ' ' + word).length > 40 && currentChunk.length > 0) {
                chunks.push(currentChunk.trim());
                currentChunk = word;
              } else {
                currentChunk = currentChunk ? currentChunk + ' ' + word : word;
              }
            }
            if (currentChunk) chunks.push(currentChunk.trim());
            return chunks;
          }
          return [line];
        });
      
      console.log('📿 분할된 줄 수:', lines.length);
      console.log('📿 분할된 줄들:', lines);
      setBlessingLines(lines);
      
      // 화면 크기에 따른 최대 줄 수 계산 (모바일: 8줄, 데스크톱: 10줄)
      const maxLines = window.innerWidth < 768 ? 8 : 10;
      setMaxLinesBeforeScroll(maxLines);
      
      // 축도 텍스트 길이에 따른 애니메이션 지속시간 계산
      const calculateBlessingDuration = (lines: string[]): number => {
        const maxLinesShown = Math.min(lines.length, maxLines);
        const remainingLines = Math.max(0, lines.length - maxLines);
        
        const pauseTime = 3; // 시작 전 3초 대기
        const lineByLineTime = maxLinesShown * 3; // 화면에 표시되는 줄들: 3초씩
        const scrollTransitionTime = remainingLines > 0 ? 0.8 : 0; // 스크롤 전환 준비 시간
        const continuousScrollTime = remainingLines * 3.6; // 연속 스크롤 시간: 남은 줄 수 × 3.6초
        const completeTime = 3; // 완료 후 3초 대기
        
        return pauseTime + lineByLineTime + scrollTransitionTime + continuousScrollTime + completeTime;
      };
      
      const duration = calculateBlessingDuration(lines);
      // setBlessingAnimationDuration removed as it was unused
      
      console.log(`📿 축도 시작 - 줄 수: ${lines.length}, 최대 표시 줄: ${maxLines}, 총 애니메이션 시간: ${duration}초`);
      console.log(`⏱️ 새로운 타이밍: 시작대기 4초 → 줄별표시 3.5초 → 스크롤 3.5초 → 완료대기 5초`);
    }
    
    // Cleanup 함수: 컴포넌트 언마운트 또는 currentStep 변경 시 타이머 정리
    return () => {
      if (hymn1Timer1) clearTimeout(hymn1Timer1);
      if (hymn1Timer2) clearTimeout(hymn1Timer2);
      if (hymn2Timer1) clearTimeout(hymn2Timer1);
      if (hymn2Timer2) clearTimeout(hymn2Timer2);
    };
    
    // 교회 권유 단계 처리는 별도 useEffect에서 단발성으로 처리하여 깜빡임 방지
  }, [currentStep, isProcessing, worshipData.blessing, currentLanguage]);

  // 교회 권유 단계: 진입 시 즉시 표시하고 4초간 정지 후 다음 단계로 이동 준비 (표시만 제어)
  useEffect(() => {
    if (currentStep !== WORSHIP_STEPS.CHURCH_INVITATION) return;
    setShowChurchInvitationMessage(true);
  }, [currentStep]);

  // 녹화 모드에서만 녹화 시작 (사용자 동의 후)
  useEffect(() => {
    if (currentStep === WORSHIP_STEPS.BLESSING && isRecordingMode && !isRecording && !recordingStartedRef.current) {
      recordingStartedRef.current = true;
      (async () => {
        try {
          await startRecording({ includeAudio: true, cropElement: blessingContainerRef.current, videoBitsPerSecond: 2_000_000, fps: 24 });
        } catch (e) {
          console.warn('Screen recording not started:', e);
        }
      })();
    }
  }, [currentStep, isRecordingMode, isRecording, startRecording]);

  // 창 리사이즈 시도 후 녹화 시작 준비
  const beginRecordingFlow = () => {
    // 기존 흐름 유지: 프롬프트 닫고 녹화 모드 on, 상태 초기화 후 축도 단계로
    setShowCreateVideoPrompt(false);
    setIsRecordingMode(true);
    recordingStartedRef.current = false;
    endCardShownRef.current = false;
    setBlessingAnimationComplete(false);
    setBlessingVerseComplete(false);
    setShowBlessingReference(false);
    setShowBlessingVerseText(false);
    setBlessingPhase('pause');
    setVisibleBlessingLines(0);
    setShowBlessingMessage(false);
    setCurrentStep(WORSHIP_STEPS.BLESSING);
  };

  const attemptResizeWindow = (width: number, height: number) => {
    try {
      const screenW = window.screen?.availWidth || window.innerWidth;
      const screenH = window.screen?.availHeight || window.innerHeight;
      const left = Math.max(0, Math.floor((screenW - width) / 2));
      const top = Math.max(0, Math.floor((screenH - height) / 2));
      try { window.moveTo(left, top); } catch (_) { /* ignore */ }
      try { window.resizeTo(width, height); } catch (_) { /* ignore */ }
    } catch (_) { /* ignore */ }
  };

  // 축도 종료 직전 엔드 카드 표시 후 녹화 종료 및 리뷰 페이지로 이동 (녹화 모드에서만)
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      useEffect(() => {
    if (
      currentStep === WORSHIP_STEPS.BLESSING &&
      showBlessingMessage &&
      !endCardShownRef.current &&
      isRecordingMode
    ) {
      endCardShownRef.current = true;
      setShowEndCard(true);
          const timer = setTimeout(async () => {
        let videoUrl: string | null = null;
        try {
          const result = await stopRecording();
          videoUrl = result?.url || null;
        } catch (e) {
          // ignore
        }
        setShowEndCard(false);
        navigate('/worship-review', {
          state: {
            videoUrl,
            genderType: location.state?.genderType || 'brother',
            // 최초 생성된 예배 데이터(축도 포함)를 리뷰 페이지로 전달하여 재생성 시 재사용
            worshipData
          }
        });
          }, 4000);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, showBlessingMessage, stopRecording, navigate, location.state, isRecordingMode]);

  // 녹화 동의 프롬프트: 축도 본문 모두 표출 후 약 2초 뒤 표시 (녹화 모드가 아닐 때만)
  useEffect(() => {
    if (currentStep !== WORSHIP_STEPS.BLESSING) return;
    if (!showBlessingMessage) return;
    if (isRecordingMode) return;
    const timer = setTimeout(() => setShowCreateVideoPrompt(true), 2000);
    return () => clearTimeout(timer);
  }, [currentStep, showBlessingMessage, isRecordingMode]);

  // 축도 단계별 애니메이션 제어
  useEffect(() => {
    console.log('📿 축도 애니메이션 제어 useEffect 실행');
    console.log('📿 currentStep:', currentStep, 'WORSHIP_STEPS.BLESSING:', WORSHIP_STEPS.BLESSING);
    console.log('📿 blessingLines.length:', blessingLines.length);
    console.log('📿 blessingPhase:', blessingPhase);
    
    if (currentStep !== WORSHIP_STEPS.BLESSING || blessingLines.length === 0) {
      console.log('📿 축도 애니메이션 제어 중단 - 조건 불충족');
      return;
    }

    let timer: NodeJS.Timeout;

    switch (blessingPhase) {
      case 'pause':
        // 1초 대기 후 줄별 표시 시작
        console.log('📿 pause 단계 - 1초 후 lineByLine으로 전환');
        timer = setTimeout(() => {
          setBlessingPhase('lineByLine');
        }, 1000);
        break;

      case 'lineByLine':
        if (visibleBlessingLines < blessingLines.length) {
          // 아직 표시할 줄이 더 있는 경우
          if (visibleBlessingLines < maxLinesBeforeScroll) {
            // 화면이 가득 차지 않았으면 계속 줄별 표시
            timer = setTimeout(() => {
              setVisibleBlessingLines(prev => prev + 1);
            }, 3500); // 3.5초마다 한 줄씩
          } else {
            // 화면이 가득 찼으면 스크롤 모드로 전환 (한 줄씩 계속 추가하면서 스크롤)
            console.log(`📺 화면이 가득참! 스크롤 모드로 전환. 현재줄: ${visibleBlessingLines}, 최대줄: ${maxLinesBeforeScroll}`);
            timer = setTimeout(() => {
              console.log('🔄 scrollUp 단계 시작');
              setBlessingPhase('scrollUp');
              // 다음 줄 추가 (스크롤과 함께)
              setVisibleBlessingLines(prev => prev + 1);
            }, 3500); // 3.5초 후 스크롤 시작
          }
        } else {
          // 모든 줄을 표시했고 스크롤이 필요없으면 완료
          setBlessingPhase('complete');
        }
        break;

      case 'scrollUp':
        // 스크롤하면서 계속 새로운 줄 추가
        if (visibleBlessingLines < blessingLines.length) {
          // 아직 표시할 줄이 더 있으면 계속 추가
          timer = setTimeout(() => {
            setVisibleBlessingLines(prev => prev + 1);
          }, 3500); // 3.5초마다 한 줄씩 추가하면서 스크롤
        } else {
          // 모든 줄 표시 완료 후 5초 대기 후 완료 (충분한 여운)
          timer = setTimeout(() => {
            console.log('축도 스크롤 완료, complete 상태로 전환');
            setBlessingPhase('complete');
          }, 5000);
        }
        break;

      case 'complete':
        // 축도 본문 완료 후 성경 구절을 순차적으로 fade in
        setBlessingAnimationComplete(true);
        
        // 1초 후 성경 구절 참조 fade in 시작
        timer = setTimeout(() => {
          setShowBlessingReference(true);
          // 3초 후 성경 구절 본문 fade in 시작
          setTimeout(() => {
            setShowBlessingVerseText(true);
            // 성경 구절 표시 완료 후 2초 뒤에 모든 애니메이션 중지
            setTimeout(() => {
              setBlessingVerseComplete(true);
              // 3초 후 메시지 표시
              setTimeout(() => {
                setShowBlessingMessage(true);
              }, 3000);
            }, 2000);
          }, 3000);
        }, 1000);
        break;
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [currentStep, blessingPhase, visibleBlessingLines, blessingLines.length, maxLinesBeforeScroll]);

  // 모바일: 성경구절이 하단에 고정된 뒤 축도 끝부분이 그 바로 위에 보이게 맞춘다
  useEffect(() => {
    if (!isMobile || currentStep !== WORSHIP_STEPS.BLESSING || !showBlessingReference) return;
    const el = blessingContainerRef.current;
    if (!el) return;
    const pinBlessingEnd = () => {
      el.scrollTop = el.scrollHeight;
    };
    pinBlessingEnd();
    const timer = window.setTimeout(pinBlessingEnd, 60);
    return () => window.clearTimeout(timer);
  }, [isMobile, currentStep, showBlessingReference, showBlessingVerseText, blessingLines.length, visibleBlessingLines]);

  // 리뷰 페이지에서 돌아온 경우 재생성 요청만 처리 (교회 권유 점프는 초기 상태에서 처리되어 깜빡임 방지)
  useEffect(() => {
    if ((location.state?.jumpToChurchInvitation || location.state?.recreateVideo) && !jumpHandledRef.current) {
      jumpHandledRef.current = true;
      if (isProcessing) setIsProcessing(false);
      if (location.state?.recreateVideo) {
        // 축도 단계로 복귀하여 다시 생성 시작
        if (location.state?.worshipData) {
          // 이전에 생성된 예배 데이터를 우선 반영하여 폴백 노출을 방지
          setWorshipData(location.state.worshipData);
        }
        setShowCreateVideoPrompt(false);
        setIsRecordingMode(true);
        recordingStartedRef.current = false;
        endCardShownRef.current = false;
        setBlessingAnimationComplete(false);
        setBlessingVerseComplete(false);
        setShowBlessingReference(false);
        setShowBlessingVerseText(false);
        setBlessingPhase('pause');
        setVisibleBlessingLines(0);
        setShowBlessingMessage(false);
        setCurrentStep(WORSHIP_STEPS.BLESSING);
      }
    }
  }, [isProcessing, location.state]);

  // 교회 권유 단계에서 자동으로 헌금 페이지로 이동 - 깜빡임 없이 4초 대기 후 전환
  useEffect(() => {
    if (currentStep === WORSHIP_STEPS.CHURCH_INVITATION && showChurchInvitationMessage) {
      // 텍스트 표시 완료 후 약 4초 뒤에 자동으로 헌금 페이지로 이동
      const timer = setTimeout(() => {
        setCurrentStep(WORSHIP_STEPS.DONATION);
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [currentStep, showChurchInvitationMessage]);

  // 각 페이지에서 3초 후 안내 메시지 표시
  useEffect(() => {
    // 메시지 상태 초기화 (INDEX는 항상 표시)
    if (currentStep !== WORSHIP_STEPS.INDEX) {
      setShowIndexMessage(false);
    }

    // INDEX 페이지에서는 메시지가 항상 표시되므로 별도 처리 불필요
  }, [currentStep, sermonCompleted]);

  // 설교 말씀 자동 진행 (15초마다)
  useEffect(() => {
    if (currentStep === WORSHIP_STEPS.SERMON && !showFullSermon && !sermonCompleted && worshipData.sermonSentences.length > 0) {
      // 타이머 초기화
      if (sermonTimerRef.current) {
        clearTimeout(sermonTimerRef.current);
      }
      
      // 마지막 문장이 아닌 경우만 자동 진행
      if (currentSentenceIndex < worshipData.sermonSentences.length - 1) {
        sermonTimerRef.current = setTimeout(() => {
          setCurrentSentenceIndex(prev => prev + 1);
        }, 15000);
      } else {
        // 마지막 문장에 도달하면 완료 상태로 설정
        setSermonCompleted(true);
      }

      return () => {
        if (sermonTimerRef.current) {
          clearTimeout(sermonTimerRef.current);
        }
      };
    }
  }, [currentStep, currentSentenceIndex, showFullSermon, sermonCompleted, worshipData.sermonSentences.length]);

  // 초기 데이터 로딩
  useEffect(() => {
    // 리뷰 페이지에서 "동영상 다시 생성"으로 복귀한 경우 초기화(API 호출) 스킵
    if (location.state?.recreateVideo) {
      console.log('⚠️ Recreate video flow detected. Skipping initialization API call.');
      return;
    }

    // 리뷰 페이지에서 "예배를 마칩니다"로 진입하여 교회 권유 단계로 점프한 경우
    // 초기 INDEX 단계로 강제 전환(setCurrentStep(INDEX))을 스킵하여 화면 깜빡임을 방지
    if (initialJumpToChurch) {
      console.log('⏭️ Jump to church invitation detected. Skipping initialization to avoid flicker.');
      return;
    }

      const currentPrayerId = location.state?.prayerId;
  const currentPrayer = location.state?.prayer;
  const currentGenderType = location.state?.genderType;
  // currentDetectedLanguage 제거됨 - 사용하지 않음
    
    // 이미 초기화가 진행 중이거나 완료된 경우 중복 실행 방지
    if (initializationRef.current || apiCallInProgressRef.current) {
      console.log('🚫 Initialization already in progress, skipping...');
      return;
    }
    
    initializationRef.current = true;
    apiCallInProgressRef.current = true;
    console.log('🚀 Starting worship initialization...');

    const initializeWorship = async () => {
      const prayerId = currentPrayerId;
      const directPrayer = currentPrayer;
      const currentWorshipType = location.state?.worshipType;
      const dailyWorship = location.state?.dailyWorship;

      // 상태 업데이트
      setWorshipType(currentWorshipType || 'personal');
      if (currentWorshipType === 'daily' && dailyWorship) {
        setIssueTitle(dailyWorship.issue_title || '');
      }

      // 오늘을 위한 예배인 경우 - MongoDB에서 이미 fetch한 데이터 사용
      if (currentWorshipType === 'daily' && dailyWorship) {
        console.log('📰 Daily Worship detected - Using pre-fetched data from MongoDB');
        console.log('📰 Daily Worship Data:', dailyWorship);
        
        // 단계별 처리 시뮬레이션만 진행 (로딩 화면 표시)
        for (let i = 0; i < processingSteps.length; i++) {
          setCurrentProcessingStep(i);
          await new Promise(resolve => setTimeout(resolve, 1200));
        }

        // MongoDB에서 가져온 worship_order를 그대로 사용
        const worshipOrder = dailyWorship.worship_order;
        const scriptureItem = worshipOrder.find((item: any) => item.type === 'scripture');
        const sermonItem = worshipOrder.find((item: any) => item.type === 'sermon');
        const hymnItems = worshipOrder.filter((item: any) => item.type === 'hymn');
        const blessingItem = worshipOrder.find((item: any) => item.type === 'blessing');
        
        console.log('📖 Scripture Item:', scriptureItem);
        console.log('📝 Sermon Item:', sermonItem);
        console.log('🎵 Hymn Items:', hymnItems);
        console.log('🙏 Blessing Item:', blessingItem);
        console.log('🙏 Blessing Item Content:', blessingItem?.content);
        console.log('🙏 Blessing Item Content Type:', typeof blessingItem?.content);

        // 기도 내용 설정 (이슈 제목 + 설명)
        const prayer = `${dailyWorship.issue_title}\n\n${dailyWorship.issue_description}`;
        
        // 설교를 문장별로 분리
        // MongoDB에서 가져온 sermon의 content는 객체 형태: {title: '...', content: '실제 텍스트'}
        const sermonContent = (typeof sermonItem?.content === 'object' && sermonItem?.content?.content) 
          ? sermonItem.content.content 
          : (sermonItem?.content || '');
        const sentences = (() => {
          const hasAsianChars = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u0E00-\u0E7F]/.test(sermonContent);
          
          if (currentLanguage === 'th' || hasAsianChars) {
            const punctuationSplit = sermonContent.split(/[.!?。！？\u0E2F\u0E46\u0E4F\u0E5A\u0E5B]/)
              .map((s: string) => s.trim())
              .filter((s: string) => s.length > 0);
            
            // 따옴표만 있는 조각을 이전 문장에 붙이기
            const mergedSentences = mergeClosingQuotes(punctuationSplit);
            
            return mergedSentences.flatMap((sentence: string) => {
              const maxLength = currentLanguage === 'th' ? 80 : 120;
              if (sentence.length > maxLength) {
                const chunks: string[] = [];
                let currentChunk = '';
                const words = currentLanguage === 'th' ? sentence.split('') : sentence.split(' ');
                
                for (const word of words) {
                  const testChunk = currentLanguage === 'th' ? currentChunk + word : (currentChunk ? currentChunk + ' ' + word : word);
                  if (testChunk.length > maxLength && currentChunk.length > 0) {
                    chunks.push(currentChunk.trim());
                    currentChunk = currentLanguage === 'th' ? word : word;
                  } else {
                    currentChunk = testChunk;
                  }
                }
                if (currentChunk.trim()) chunks.push(currentChunk.trim());
                return chunks.length > 0 ? chunks : [sentence];
              }
              return [sentence];
            });
          } else {
            const splitSentences = sermonContent.split(/[.!?。！？]/)
              .map((s: string) => s.trim())
              .filter((s: string) => s.length > 0);
            
            // 따옴표만 있는 조각을 이전 문장에 붙이기
            const mergedSentences = mergeClosingQuotes(splitSentences);
            
            return mergedSentences;
          }
        })();

        console.log('📄 Parsed sentences:', sentences);
        
        // MongoDB에서 가져온 데이터의 구조 처리
        // scripture.content = {content: '...', reference: '...', reason: '...', application: '...'}
        const scriptureContent = (typeof scriptureItem?.content === 'object' && scriptureItem?.content?.content)
          ? scriptureItem.content.content
          : (scriptureItem?.content || '');
        const scriptureReference = (typeof scriptureItem?.content === 'object' && scriptureItem?.content?.reference)
          ? scriptureItem.content.reference
          : (scriptureItem?.reference || '');
        
        // blessing.content = {content: {content: '...', biblical_basis: '...', ...}}
        const blessingData = (typeof blessingItem?.content === 'object' && blessingItem?.content?.content)
          ? blessingItem.content.content
          : (blessingItem?.content || {});
        
        // Worship data 설정
        // Daily worship의 찬송가 데이터는 content 객체 안에 있음
        const hymn1Content = hymnItems[0]?.content || hymnItems[0];
        const hymn2Content = hymnItems[1]?.content || hymnItems[1];
        
        setWorshipData({
          prayer,
          bibleVerse: scriptureContent,
          bibleReference: scriptureReference,
          sermon: sermonContent,
          sermonSentences: sentences,
          sermonStyle: sermonItem?.content?.style,
          sermonPastor: sermonItem?.content?.pastor,
          hymn1: hymn1Content ? {
            title: hymn1Content.title || '',
            thumbnail: hymn1Content.thumbnail || '',
            videoUrl: hymn1Content.video_url || '',
            channel: hymn1Content.channel || t('worship.hymn.defaultChannel'),
            duration: hymn1Content.duration || ''
          } : {
            title: '주는 은혜와 사랑이',
            thumbnail: 'https://i.ytimg.com/vi/Mn6CDjp_3LY/mqdefault.jpg',
            videoUrl: 'https://www.youtube.com/embed/Mn6CDjp_3LY',
            channel: t('worship.hymn.defaultChannel'),
            duration: '3:45'
          },
          hymn2: hymn2Content ? {
            title: hymn2Content.title || '',
            thumbnail: hymn2Content.thumbnail || '',
            videoUrl: hymn2Content.video_url || '',
            channel: hymn2Content.channel || t('worship.hymn.defaultChannel'),
            duration: hymn2Content.duration || ''
          } : {
            title: '나의 기도하는 그 시간',
            thumbnail: 'https://i.ytimg.com/vi/YwAquUTgNkQ/mqdefault.jpg',
            videoUrl: 'https://www.youtube.com/embed/YwAquUTgNkQ',
            channel: t('worship.hymn.defaultChannel'),
            duration: '4:12'
          },
          blessing: {
            content: (typeof blessingData === 'object' ? blessingData.content : blessingData) || '',
            biblical_basis: (typeof blessingData === 'object' ? blessingData.biblical_basis : '') || '',
            personal_application: (typeof blessingData === 'object' ? blessingData.personal_application : '') || '',
            blessing_verse: (typeof blessingData === 'object' ? blessingData.blessing_verse : '') || '',
            blessing_reference: (typeof blessingData === 'object' ? blessingData.blessing_reference : '') || scriptureReference,
            pastor: blessingItem?.content?.pastor,
            style: blessingItem?.content?.style
          },
          blessingReference: (typeof blessingData === 'object' ? blessingData.blessing_reference : '') || scriptureReference
        });
        
        console.log('✅ Daily Worship data loaded from MongoDB');
        apiCallInProgressRef.current = false;
        setIsProcessing(false);
        setCurrentStep(WORSHIP_STEPS.INDEX);
        console.log('🏠 Set to INDEX step');
        return;
      }

      // 나만을 위한 예배 - 기존 로직 (AI API 호출)
      let prayer = '';

      if (prayerId) {
        try {
          const response = await fetch(getApiUrl(`prayers/anonymous/${prayerId}`));
          if (response.ok) {
            const data = await response.json();
            prayer = data.prayer.content;
          } else {
            console.error('Failed to fetch prayer from database');
            apiCallInProgressRef.current = false;
            initializationRef.current = false;
            navigate('/');
            return;
          }
        } catch (error) {
          console.error('Error fetching prayer:', error);
          apiCallInProgressRef.current = false;
          initializationRef.current = false;
          navigate('/');
          return;
        }
      } else if (directPrayer) {
        prayer = directPrayer;
      } else {
        // 테스트를 위해 기본값 사용
        prayer = '하나님, 오늘도 저에게 은혜를 베풀어 주시고 평안한 하루가 되게 해주세요.';
      }

      console.log('🙏 Using prayer:', prayer);
      setWorshipData(prev => ({ ...prev, prayer }));

      setHasLiveProgress(true);
      setCurrentProcessingStep(0);

      const apiCallPromise = (async () => {
        try {
          // API 호출 중복 방지를 위한 추가 체크 + 응답 캐싱
          const prayerKey = prayer.slice(0, 50).replace(/\s+/g, '_');
          const apiCallKey = `api_call_${prayerKey}`;
          const apiResponseKey = `api_response_${prayerKey}`;
          const lastApiCall = sessionStorage.getItem(apiCallKey);
          const apiCallTime = Date.now();
          
          // 2분 이내에 같은 기도 내용으로 API 호출했다면 캐시 사용 시 스킵
          if (lastApiCall && (apiCallTime - parseInt(lastApiCall)) < 120000) {
            const cached = sessionStorage.getItem(apiResponseKey);
            if (cached) {
              console.log('🗄️ Using cached API response for this prayer.');
              try {
                return JSON.parse(cached);
              } catch (_) {
                // 캐시 파싱 실패 시 계속 진행하여 새로 호출
              }
            }
            console.log('⚠️ No cached response found; proceeding with API call despite cooldown.');
          }

          console.log('📡 Making API call to crew-worship...');
            
          // 백엔드 API 호출로 실제 예배 데이터 생성 (다국어 지원)
          // language 파라미터를 전달하지 않아 백엔드에서 자체 언어 감지 수행
          const requestBody = {
            prayer_content: prayer,
            gender_type: currentGenderType || 'brother', // 기본값은 형제님
            worship_mode: currentWorshipType === 'daily' ? 'daily' : 'personal' // 예배 모드 추가
          };

          let hasStreamStarted = false;
          let result: WorshipStreamResult;
          try {
            result = await requestWorshipWithProgress(requestBody, stage => {
              hasStreamStarted = true;
              const stepIndex = GENERATION_STAGE_INDEX[stage];
              if (stepIndex !== undefined) setCurrentProcessingStep(stepIndex);
            });
          } catch (streamError) {
            // 생성이 이미 시작된 뒤 끊긴 경우 중복 생성을 막기 위해 재요청하지 않음
            if (hasStreamStarted) {
              console.error('❌ Worship stream interrupted:', streamError);
              return null;
            }
            console.warn('⚠️ SSE unavailable, falling back to simple-worship:', streamError);
            const response = await fetch(getApiUrl('ai/simple-worship'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestBody),
            });
            result = { status: response.status, data: await response.json().catch(() => ({})) };
          }

          if (result.status >= 200 && result.status < 300) {
            const data = result.data;
            // 성공 시에만 쿨다운 및 응답 캐시 저장
            sessionStorage.setItem(apiCallKey, apiCallTime.toString());
            try {
              sessionStorage.setItem(apiResponseKey, JSON.stringify(data));
            } catch (_) {
              // 저장 실패는 무시
            }
            return data;
          } else {
            // 에러 응답 처리 - 쿨다운을 적용하지 않음 (재시도 가능)
            const errorData = result.data || {};
            if (errorData.error === 'Prayer content too short') {
              alert(errorData.message || '기도 내용이 너무 짧습니다. 최소 10자 이상 입력해 주세요.');
            }
            return null;
          }
        } catch (error) {
          console.error('Failed to fetch worship data:', error);
          return null;
        }
      })();

      // 서버의 실제 진행 단계(SSE)가 모달에 반영되므로 API 응답만 대기
      const apiData = await apiCallPromise;

      // API 데이터가 있으면 처리
      if (apiData?.worship?.worship_order) {
        console.log('🔍 API Response received:', apiData);
        console.log('🔍 Full API Response JSON:', JSON.stringify(apiData, null, 2));
        console.log('🔍 Worship Order:', apiData.worship.worship_order);
        
        const worshipOrder = apiData.worship.worship_order;
        const scriptureItem = worshipOrder.find((item: any) => item.type === 'scripture');
        const sermonItem = worshipOrder.find((item: any) => item.type === 'sermon');
        const hymnItems = worshipOrder.filter((item: any) => item.type === 'hymn');
        const blessingItem = worshipOrder.find((item: any) => item.type === 'blessing');
        
        console.log('🔍 Blessing Item Found:', blessingItem);
        console.log('🔍 Blessing Reference from API:', blessingItem?.reference);

        console.log('📖 Scripture Item:', scriptureItem);
        console.log('📖 Scripture Item JSON:', JSON.stringify(scriptureItem, null, 2));
        console.log('📝 Sermon Item:', sermonItem);
        console.log('📝 Sermon Item JSON:', JSON.stringify(sermonItem, null, 2));
        console.log('🎵 Hymn Items:', hymnItems);
        console.log('🎵 Hymn Items JSON:', JSON.stringify(hymnItems, null, 2));
        console.log('🎵 First Hymn:', hymnItems[0]);
        console.log('🎵 Second Hymn:', hymnItems[1]);
        console.log('🙏 Blessing Item:', blessingItem);

        // 설교를 문장별로 분리 (다국어 지원)
        const sermonContent = sermonItem?.content || '';
        console.log('📝 Sermon Content:', sermonContent);
        
        // 다국어 문장 분리 로직 (태국어 등 구두점이 적은 언어 지원)
        const sentences = (() => {
          // 태국어나 기타 아시아 언어가 포함된 경우 세밀한 분할
          const hasAsianChars = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u0E00-\u0E7F]/.test(sermonContent);
          
          if (currentLanguage === 'th' || hasAsianChars) {
            // 1단계: 구두점으로 분할 (태국어 구두점 포함)
            const punctuationSplit = sermonContent.split(/[.!?。！？\u0E2F\u0E46\u0E4F\u0E5A\u0E5B]/)
              .map((s: string) => s.trim())
              .filter((s: string) => s.length > 0);
            
            // 따옴표만 있는 조각을 이전 문장에 붙이기
            const mergedSentences = mergeClosingQuotes(punctuationSplit);
            
            // 2단계: 긴 문장을 적절한 길이로 분할
            return mergedSentences.flatMap((sentence: string) => {
              // 태국어의 경우 더 짧은 길이로 분할 (공백이 없어서 짧게 나눠야 함)
              const maxLength = currentLanguage === 'th' ? 80 : 120;
              const forceBreakLength = currentLanguage === 'th' ? 100 : 150;
              
              if (sentence.length > maxLength) {
                const chunks: string[] = [];
                let currentChunk = '';
                
                // 단어나 문자 단위로 분할
                const words = currentLanguage === 'th' ? 
                  sentence.split('') : // 태국어는 문자 단위
                  sentence.split(' '); // 기타 언어는 단어 단위
                
                for (let i = 0; i < words.length; i++) {
                  const word = words[i];
                  const testChunk = currentLanguage === 'th' ?
                    currentChunk + word : // 태국어는 공백 없이
                    (currentChunk ? currentChunk + ' ' + word : word); // 기타 언어는 공백으로 구분
                  
                  if (testChunk.length > maxLength && currentChunk.length > 0) {
                    // 자연스러운 분할점 찾기
                    let isNaturalBreakPoint = false;
                    
                    if (currentLanguage === 'th') {
                      // 태국어: 특정 문자 앞에서 분할 (ที่, และ, เป็น, มี 등)
                      const nextChars = words.slice(i, i + 3).join('');
                      isNaturalBreakPoint = /^[ที่และเป็นมีจากเมื่อแต่ซึ่ง]/.test(nextChars);
                    } else {
                      // 기타 언어: 접속사나 전치사 앞에서 분할
                      isNaturalBreakPoint = /^(and|or|but|with|from|to|for|in|on|at|the|a|an)$/i.test(word);
                    }
                    
                    if (isNaturalBreakPoint || currentChunk.length >= forceBreakLength) {
                      chunks.push(currentChunk.trim());
                      currentChunk = currentLanguage === 'th' ? word : word;
                    } else {
                      currentChunk = testChunk;
                    }
                  } else {
                    currentChunk = testChunk;
                  }
                }
                
                if (currentChunk.trim()) {
                  chunks.push(currentChunk.trim());
                }
                
                return chunks.length > 0 ? chunks : [sentence];
              }
              
              return [sentence];
            });
          } else {
            // 기존 로직: 한국어, 영어, 일본어, 중국어 문장 구분자로 분리
            const splitSentences = sermonContent.split(/[.!?。！？]/)
              .map((s: string) => s.trim())
              .filter((s: string) => s.length > 0);
            
            // 따옴표만 있는 조각을 이전 문장에 붙이기
            const mergedSentences = mergeClosingQuotes(splitSentences);
            
            return mergedSentences;
          }
        })();

        console.log('📄 Parsed sentences:', sentences);
        
        setWorshipData(prev => ({
          ...prev,
          analysis: apiData.worship?.analysis || prev.analysis,
          bibleVerse: scriptureItem?.content || '여호와는 나의 목자시니 내게 부족함이 없으리로다. 그가 나를 푸른 풀밭에 누이시며 쉴 만한 물 가로 인도하시는도다.',
          bibleReference: scriptureItem?.reference || '시편 23:1-2',
          sermon: sermonContent || `사랑하는 형제자매 여러분, 오늘 당신이 올려드린 기도를 통해 하나님과의 특별한 시간을 갖고자 합니다. 이러한 당신의 마음을 하나님께서는 이미 잘 알고 계십니다. 하나님께서는 우리의 모든 필요와 고민을 아시고, 때로는 우리가 생각지도 못한 방법으로 응답해주십니다. 오늘도 하나님의 사랑과 은혜가 당신과 함께 하시기를 기도합니다.`,
          sermonSentences: sentences.length > 0 ? sentences : [
            '사랑하는 형제자매 여러분, 오늘 당신이 올려드린 기도를 통해 하나님과의 특별한 시간을 갖고자 합니다',
            '이러한 당신의 마음을 하나님께서는 이미 잘 알고 계십니다',
            '하나님께서는 우리의 모든 필요와 고민을 아시고, 때로는 우리가 생각지도 못한 방법으로 응답해주십니다',
            '오늘도 하나님의 사랑과 은혜가 당신과 함께 하시기를 기도합니다'
          ],
          sermonStyle: sermonItem?.style || undefined,
          sermonPastor: sermonItem?.pastor || undefined,
          hymn1: hymnItems[0] ? {
            title: hymnItems[0].title || '주는 은혜와 사랑이',
            thumbnail: (hymnItems[0].thumbnail && hymnItems[0].thumbnail.trim() !== '') ? hymnItems[0].thumbnail : 'https://i.ytimg.com/vi/Mn6CDjp_3LY/mqdefault.jpg',
            videoUrl: hymnItems[0].video_url || 'https://www.youtube.com/embed/Mn6CDjp_3LY',
            channel: hymnItems[0].channel || t('worship.hymn.defaultChannel'),
            duration: hymnItems[0].duration || '3:45'
          } : prev.hymn1,
          hymn2: hymnItems[1] ? (() => {
            const hymn2Data = {
              title: hymnItems[1].title || '나의 기도하는 그 시간',
              thumbnail: (hymnItems[1].thumbnail && hymnItems[1].thumbnail.trim() !== '') ? hymnItems[1].thumbnail : 'https://i.ytimg.com/vi/YwAquUTgNkQ/mqdefault.jpg',
              videoUrl: hymnItems[1].video_url || 'https://www.youtube.com/embed/YwAquUTgNkQ',
              channel: hymnItems[1].channel || t('worship.hymn.defaultChannel'),
              duration: hymnItems[1].duration || '4:12'
            };
            console.log('🎵 Setting hymn2 data:', hymn2Data);
            console.log('🎵 Original hymnItems[1]:', hymnItems[1]);
            return hymn2Data;
          })() : prev.hymn2,
          blessing: {
            content: blessingItem?.content || prev.blessing.content,
            biblical_basis: blessingItem?.biblical_basis || prev.blessing.biblical_basis,
            personal_application: blessingItem?.personal_application || prev.blessing.personal_application,
            blessing_verse: blessingItem?.blessing_verse || prev.blessing.blessing_verse,
            blessing_reference: blessingItem?.reference || prev.blessing.blessing_reference,
            pastor: blessingItem?.pastor || prev.blessing.pastor,
            style: blessingItem?.style || prev.blessing.style
          },
          blessingReference: blessingItem?.reference || prev.blessingReference
        }));
        
        console.log('✅ Worship Data Updated:');
        console.log('   - blessing_reference:', blessingItem?.reference);
        console.log('   - blessingReference:', blessingItem?.reference);
      } else {
        // API 응답이 없거나 worship_order가 없는 경우
        console.error('❌ No API response or worship_order missing:', apiData);
        console.error('❌ Full API Response:', JSON.stringify(apiData, null, 2));
      }

      // API 호출 완료 상태로 설정
      apiCallInProgressRef.current = false;
      console.log('📝 API call completed, flag reset');

      console.log('✅ Worship initialization complete');
      setIsProcessing(false);
      setCurrentStep(WORSHIP_STEPS.INDEX);
      console.log('🏠 Set to INDEX step');
    };

    initializeWorship();

    // cleanup function - 컴포넌트 언마운트 시 초기화 상태 리셋
    return () => {
      initializationRef.current = false;
      apiCallInProgressRef.current = false;
      if (sermonTimerRef.current) {
        clearTimeout(sermonTimerRef.current);
      }
      // sessionStorage는 유지하여 새로고침 시에도 중복 방지
      console.log('🧹 Cleanup: All refs reset');
    };
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [location.state?.prayerId, location.state?.prayer, location.state?.genderType, location.state?.detectedLanguage, location.state?.worshipType, location.state?.dailyWorship, currentLanguage, t, navigate, processingSteps.length]);

  // 텍스트에서 markdown과 numbering, prompt 내용 제거하는 함수
  const cleanText = (text: string | undefined | null) => {
    // undefined, null, 빈 문자열 처리
    if (!text || typeof text !== 'string') {
      return '';
    }
    
    return text
      // markdown bold 제거: **text** -> text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      // markdown italic 제거: *text* -> text
      .replace(/\*(.*?)\*/g, '$1')
      // markdown 헤더 제거: ### text -> text
      .replace(/^#{1,6}\s*/gm, '')
      // numbering 제거: 1. text -> text, 2) text -> text
      .replace(/^\d+[.)]\s*/gm, '')
      // 리스트 제거: - text -> text
      .replace(/^[-*+]\s*/gm, '')
      // 설교 중간제목/분류제목 제거: 도입:, 성경말씀 해석:, 실천적 가르침:, 결론:, 적용: 등
      /* eslint-disable-next-line no-useless-escape */
      .replace(/^\s*[\[\(\{\u3010\u3014\uFF3B]?\s*(도입|서론|성경\s*말씀\s*해석|말씀\s*해석|실천적\s*가르침|적용|결론)\s*[\]\)\}\u3011\u3015\uFF3D]?\s*[:：]\s*/gmi, '')
      // 문장/행의 맨 앞에 오는 일반 레이블 패턴 제거 (예: 영적 적용:, 실천적 해석:, Introduction:)
      // - 숫자/절 표기(예: 요 3:16) 등은 보존하기 위해 시작 토큰에 숫자를 허용하지 않음
      /* eslint-disable-next-line no-useless-escape */
      .replace(/^\s*(?:[\[\(\{（【]?\s*)?(?:[가-힣A-Za-z]+(?:\s+[가-힣A-Za-z]+){0,5})\s*[:：]\s*/gm, '')
      // prompt 형태의 불필요한 내용 제거
      .replace(/무엇무엇\.{2,}/g, '')
      .replace(/\.\.\./g, '')
      .replace(/---+/g, '')
      .replace(/===+/g, '')
      .replace(/\*{3,}/g, '')
      // 메타 정보나 지시문 제거
      .replace(/^(참고|주의|알림|메모|힌트|팁):\s*/gmi, '')
      .replace(/^\[(.*?)\]\s*/gm, '')
      .replace(/^(다음|위|아래|이전)\s*(내용|정보|단계).*?:/gmi, '')
      // AI 응답 형태의 텍스트 제거
      .replace(/^(물론|당연히|확실히|알겠습니다|네,?\s)/gmi, '')
      .replace(/^(이\s*(문제|상황|경우)에\s*(대해|관해))/gmi, '')
      // 여러 개의 연속된 공백을 하나로
      .replace(/\s+/g, ' ')
      // 연속된 줄바꿈 정리
      .replace(/\n\s*\n/g, '\n')
      // 앞뒤 공백 제거
      .trim();
  };

  // 간단하고 안정적인 배경 컨테이너 - 깜빡임 완전 제거
  const BackgroundContainer: React.FC<{
    children: React.ReactNode;
    overlay?: string;
    className?: string;
    sx?: any;
  }> = ({ children, overlay = 'rgba(255, 255, 255, 0.3)', className, sx = {} }) => (
    <Box 
      className={className}
      sx={{
        minHeight: '100vh',
        backgroundColor: 'transparent',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        // 모든 전환 효과와 애니메이션 제거하여 깜빡임 방지
        transition: 'none',
        animation: 'none',
        ...sx // 추가 스타일 적용
      }}
    >
      {children}
    </Box>
  );

  // Removed unused playAudio function

  // 로딩 화면
  if (isProcessing) {
    return (
      <BackgroundContainer>
        <SEO 
          title={currentLanguage === 'ko' ? '예배 준비 중' : 'Preparing Worship'}
          description={currentLanguage === 'ko' 
            ? '당신을 위한 맞춤 예배를 준비하고 있습니다. AI가 기도 내용을 분석하여 성경 말씀, 찬송가, 설교를 준비합니다.'
            : 'Preparing a personalized worship for you. AI analyzes your prayers and prepares scripture, hymns, and sermons.'}
          url="/worship"
          locale={currentLanguage === 'ko' ? 'ko_KR' : 'en_US'}
          noindex={true}
          structuredData={getWorshipServiceData(currentLanguage as 'ko' | 'en')}
        />
        <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative', zIndex: 10 }}>
          <Box sx={{ p: 6, borderRadius: 4, backgroundColor: 'transparent', border: '2px solid white' }}>
            <Box sx={{ mb: 4 }}>
              <Box
                component="img"
                src={getImageUrl('hands.png')}
                alt="기도하는 손"
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 3,
                  filter: 'brightness(0) invert(1)',
                  animation: 'prayingHandsPulse 2s ease-in-out infinite',
                  '@keyframes prayingHandsPulse': {
                    '0%, 100%': { 
                      opacity: 0.6,
                      transform: 'scale(1)'
                    },
                    '50%': { 
                      opacity: 1,
                      transform: 'scale(1.05)'
                    }
                  }
                }}

              />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
                {t('worship.preparingTitle')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'medium', color: 'white' }}>
                {loadingSteps[currentProcessingStep]}
              </Typography>
              {hasLiveProgress && (
                <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.85)' }}>
                  {t('worship.processing.meditation')}
                </Typography>
              )}
            </Box>
            
            <Box 
              sx={{ 
                width: '100%',
                height: 12, 
                borderRadius: 6,
                backgroundColor: 'grey.200',
                overflow: 'hidden',
                mb: 2
              }}
            >
              <Box
                sx={{
                  width: `${((currentProcessingStep + 1) / loadingSteps.length) * 100}%`,
                  height: '100%',
                  background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  borderRadius: 6,
                  transition: 'width 0.8s ease'
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              {currentProcessingStep + 1} / {loadingSteps.length}
            </Typography>
          </Box>
        </Container>
      </BackgroundContainer>
    );
  }

  // 인덱스 페이지 (배경에 엷은 레이어)
  if (currentStep === WORSHIP_STEPS.INDEX) {
    return (
      <BackgroundContainer>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10, textAlign: 'center', px: 3 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 'bold', 
              color: 'white',
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            {worshipType === 'daily' ? t('worship.dailyTitle') : t('worship.personalTitle')}
          </Typography>
          <Typography variant="h5" sx={{ color: 'white', mb: 4 }}>
            {worshipType === 'daily' && issueTitle 
              ? `${t('worship.dailySubtitlePrefix')}${issueTitle}`
              : (() => {
                  const subtitle = t('worship.personalSubtitle');
                  const theme = worshipData.analysis?.theme;
                  
                  if (theme) {
                    // 언어에 따라 적절한 위치에 주제 삽입
                    if (currentLanguage === 'ko') {
                      // "오직 당신만을 위해 특별히 준비된 예배입니다" -> "오직 당신만을 위해 특별히 준비된 {주제} 예배입니다"
                      return subtitle.replace('예배입니다', `${theme} 예배입니다`);
                    } else {
                      // "A special worship service prepared exclusively for you" -> "A special {theme} worship service prepared exclusively for you"
                      return subtitle.replace('worship service', `${theme} worship service`);
                    }
                  }
                  return subtitle;
                })()
            }
          </Typography>
          {/* 예배 순서 상단 메시지 박스 */}
          {(() => {
            const pastorNameForNotice = worshipData.sermonPastor?.name || worshipData.blessing?.pastor?.name;
            if (!pastorNameForNotice) return null;
            const message = (() => {
              if (currentLanguage === 'en') {
                return `This worship has been prepared in the tone and flow of Pastor ${pastorNameForNotice}.`;
              }
              // Korean display rules
              const name = (pastorNameForNotice || '').trim();
              const includesKoreanPastorTitle = name.includes('목사');
              const isKoreanText = /[가-힣]/.test(name);
              const formattedKoName = (isKoreanText && includesKoreanPastorTitle)
                ? (name.endsWith('님') ? name : `${name} 님`)
                : `${name} 목사님`;
              return `이 예배는 ${formattedKoName}의 어조와 흐름으로 준비됐습니다.`;
            })();
            return (
              <Box sx={{
                backgroundColor: 'rgba(74, 55, 40, 0.85)',
                borderRadius: 2,
                p: 2,
                mb: 2,
                
                boxShadow: '0 0 20px rgba(255,255,255,0.22)',
                animation: 'softPulse 5s ease-in-out infinite',
                '@keyframes softPulse': {
                  '0%': { boxShadow: '0 0 18px rgba(255,255,255,0.22)' },
                  '50%': { boxShadow: '0 0 44px rgba(255,255,255,0.46)' },
                  '100%': { boxShadow: '0 0 18px rgba(255,255,255,0.22)' }
                }
              }}>
                <Typography variant="body1" sx={{ color: '#ffffff' }}>
                  {message}
                </Typography>
              </Box>
            );
          })()}
          
          <Box 
            sx={{ 
              backgroundColor: 'transparent', 
              border: '2px solid white',
              borderRadius: 2, 
              p: 3, 
              textAlign: 'left',
              animation: 'fadeInCard 1.5s ease-out',
              '@keyframes fadeInCard': {
                from: { opacity: 0, transform: 'translateY(30px) scale(0.95)' },
                to: { opacity: 1, transform: 'translateY(0) scale(1)' }
              }
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'semibold', color: 'white', mb: 2 }}>
              {t('worship.order')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { step: 1, title: t('worship.scripture'), bgColor: '#8B7355', textColor: '#4a3728' },
                { step: 2, title: t('worship.praise'), bgColor: '#A0956B', textColor: '#4a3728' },
                { step: 3, title: t('worship.sermonMessage'), bgColor: '#7A6448', textColor: '#fff' },
                { step: 4, title: t('worship.praise'), bgColor: '#9A8A6D', textColor: '#4a3728' },
                { step: 5, title: t('worship.blessingTitle'), bgColor: '#6B5D47', textColor: '#fff' }
              ].map(({ step, title, bgColor, textColor }) => (
                <Box key={step} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: bgColor,
                      color: textColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 'semibold',
                      mr: 2
                    }}
                  >
                    {step}
                  </Box>
                  <Typography sx={{ color: 'white' }}>{title}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          
          

          {/* 우하단 안내 메시지 - 깜빡임 없는 부드러운 등장 */}
          {showIndexMessage && (
            <Box
              sx={{
                position: 'fixed',
                bottom: 20,
                right: 20,
                backgroundColor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                borderRadius: 3,
                px: 3,
                py: 2,
                zIndex: 1000,
                opacity: 1, // 즉시 표시하여 깜빡임 완전 방지
              }}
            >
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium' }}>
                {(() => {
                  if (!isMobile) return t('worship.worshipMessage');
                  const mobileText = t('worship.worshipMessageMobile');
                  return mobileText === 'worship.worshipMessageMobile' ? t('worship.worshipMessage') : mobileText;
                })()}
              </Typography>
            </Box>
          )}
        </Container>
      </BackgroundContainer>
    );
  }

  // 성경말씀 페이지 (fade text)
  if (currentStep === WORSHIP_STEPS.SCRIPTURE) {
    return (
      <BackgroundContainer overlay="rgba(0, 0, 0, 0.3)">
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10, textAlign: 'center', px: 3 }}>
              <Typography 
            sx={{ 
              color: 'white',
              fontWeight: 'bold',
              mb: 6,
              fontSize: { xs: '2.4rem', md: '3.2rem' },
              wordBreak: 'keep-all',
              overflowWrap: 'normal',
              hyphens: 'none',
              animation: 'fadeIn 1s ease-out',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(20px)' },
                to: { opacity: 1, transform: 'translateY(0)' }
              }
            }}
          >
            {t('worship.scripture')}
          </Typography>
          
          <Typography 
            sx={{ 
              color: 'white',
              lineHeight: 1.8,
              mb: 4,
              fontSize: { xs: '2rem', md: '2.8rem' },
              fontWeight: 400,
              fontFamily: 'serif',
              wordBreak: 'keep-all',
              overflowWrap: 'break-word',
              hyphens: 'none',
              textWrap: 'pretty',
              hangingPunctuation: 'first last',
              animation: 'fadeIn 1.5s ease-out 0.5s both',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
              '& .quote-content': {
                display: 'inline',
                whiteSpace: 'pre-wrap'
              },
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(30px)' },
                to: { opacity: 1, transform: 'translateY(0)' }
              }
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>"</span>
            <span className="quote-content">{(() => {
              const base = worshipData.bibleVerse;
              return currentLanguage === 'en' ? cleanEnglishPastorSuffix(base) : base;
            })()}</span>
            <span style={{ whiteSpace: 'nowrap' }}>"</span>
          </Typography>
          
          <Typography 
            sx={{ 
              color: 'white', 
              fontWeight: 'medium',
              fontSize: { xs: '1.4rem', md: '2rem' },
                                wordBreak: { xs: 'break-all', md: 'keep-all' },
                  overflowWrap: { xs: 'break-word', md: 'normal' },
                  hyphens: 'none',
                  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
              animation: 'fadeIn 1.5s ease-out 1s both',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(20px)' },
                to: { opacity: 1, transform: 'translateY(0)' }
              }
            }}
          >
            {worshipData.bibleReference}
          </Typography>
          
          {/* 성경말씀 우하단 버튼 제거됨 */}
        </Container>
      </BackgroundContainer>
    );
  }

  // 첫번째 찬송가 페이지
  if (currentStep === WORSHIP_STEPS.HYMN1) {
    return (
      <BackgroundContainer 
        overlay="rgba(0, 0, 0, 0.3)" 
        sx={{ p: 3 }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'white', mb: 2 }}>{t('worship.praise')}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'semibold', color: 'white' }}>{worshipData.hymn1.title}</Typography>
          </Box>
          
          <Box sx={{ backgroundColor: 'transparent', border: '2px solid white', borderRadius: 4, p: 4 }}>
            <Box 
              sx={{ 
                aspectRatio: '16/9', 
                maxWidth: '800px', 
                mx: 'auto', 
                borderRadius: 2, 
                overflow: 'hidden',
                boxShadow: 2,
                position: 'relative',
                cursor: firstHymnPlaying ? 'default' : 'pointer'
              }}
              onClick={() => {
                if (!firstHymnPlaying && !firstHymnShowingPlaceholder) {
                  setFirstHymnUserInitiated(true);
                  setFirstHymnShowingPlaceholder(true);
                  setTimeout(() => {
                    setFirstHymnShowingPlaceholder(false);
                    setFirstHymnPlaying(true);
                  }, 2000);
                }
              }}
            >
              {/* YouTube 동영상 - 썸네일이 사라진 후에만 로드 및 재생 */}
              {firstHymnPlaying && (
                <iframe
                  width="100%"
                  height="100%"
                  src={(() => {
                    const embedUrl = convertToEmbedUrl(worshipData.hymn1.videoUrl);
                    const separator = embedUrl.includes('?') ? '&' : '?';
                    return `${embedUrl}${separator}autoplay=0&rel=0&modestbranding=1&controls=1&showinfo=0&iv_load_policy=3&loop=0&disablekb=0&fs=1&enablejsapi=1&playsinline=1`;
                  })()}
                  style={{ 
                    borderRadius: '8px',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: 1,
                    zIndex: 1
                  }}
                  allowFullScreen
                  title="찬송가 비디오"
                  allow="autoplay; encrypted-media; gyroscope; picture-in-picture; clipboard-write; web-share"
                  sandbox="allow-same-origin allow-scripts allow-forms allow-presentation"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              )}
              
              {firstHymnShowingPlaceholder && (
                // Placeholder 이미지 표시 - play button 제거, 3초 후 페이드아웃
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${getImageUrl('youtube_thumbnail.png')})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: '8px',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: firstHymnThumbnailFading ? 0 : 1,
                    transition: 'opacity 1s ease-out',
                    zIndex: 2
                  }}
                />
              )}
              
              {!firstHymnPlaying && !firstHymnShowingPlaceholder && (
                <>
                  {worshipData.hymn1.thumbnail ? (
                    <img 
                      src={worshipData.hymn1.thumbnail}
                      alt={worshipData.hymn1.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        // 썸네일 로드 실패 시 기본 YouTube 썸네일로 교체
                        const img = e.currentTarget as HTMLImageElement;
                        if (!img.src.includes('youtube_thumbnail.png')) {
                          img.src = getImageUrl('youtube_thumbnail.png');
                        } else {
                          // 기본 이미지도 실패 시 gradient 배경으로 교체
                          img.style.display = 'none';
                          img.parentElement!.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%', 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: 'white', fontSize: '4rem' }}>🎵</Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      width: 80,
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        transform: 'translate(-50%, -50%) scale(1.1)',
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Typography sx={{ color: 'white', fontSize: '2rem' }}>▶</Typography>
                  </Box>
                </>
              )}
            </Box>
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>{t('worship.hymn.channel')}: {worshipData.hymn1.channel}</Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>{t('worship.hymn.duration')}: {worshipData.hymn1.duration}</Typography>

              </Box>
            </Box>
          </Box>
          
          {/* 찬송가1 우하단 버튼 제거됨 */}
        </Container>
      </BackgroundContainer>
    );
  }

  // 설교 말씀 페이지 (fade text + 한 문장씩)
  if (currentStep === WORSHIP_STEPS.SERMON) {
    return (
      <BackgroundContainer 
        overlay="rgba(0, 0, 0, 0.3)"
        sx={{
          alignItems: showFullSermon ? 'flex-start' : 'center',
          overflow: showFullSermon ? 'auto' : 'hidden',
          ...(!showFullSermon && {
            flexDirection: 'column',
            justifyContent: 'flex-start',
            '&::before': { content: '""', flex: 7 },
            '&::after': { content: '""', flex: 8 }
          })
        }}
      >
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10, textAlign: 'center', px: 3 }}>
          <Box sx={{ backgroundColor: 'transparent', border: '2px solid white', borderRadius: 4, p: { xs: 4, md: 6 } }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white', mb: 2 }}>
              {t('worship.sermonMessage')}
            </Typography>
            
            {/* 설교 화면에서 목사/스타일 정보 UI 제거 */}
            
            {showFullSermon ? (
              // 모두 보기 모드
              <Box sx={{ maxHeight: '70vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', overscrollBehavior: 'contain' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'white',
                    lineHeight: 1.8,
                    textAlign: 'left',
                    fontFamily: '"Nunito Sans", "Gowun Dodum", "Roboto", "Helvetica", "Arial", sans-serif',
                    wordBreak: { xs: 'break-word', md: 'keep-all' },
                    overflowWrap: { xs: 'break-word', md: 'normal' },
                    hyphens: 'none',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {worshipData.sermonSentences.map((sentence, index) => (
                    <span key={index}>
                      {cleanText(sentence)}{index < worshipData.sermonSentences.length - 1 ? '.' : ''}
                      {index < worshipData.sermonSentences.length - 1 && (
                        <>
                          <br />
                          <br />
                        </>
                      )}
                    </span>
                  ))}
                </Typography>
              </Box>
            ) : (
              // 문장별 보기 모드
              <Box sx={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: 'white',
                    lineHeight: 1.8,
                    fontFamily: '"Nunito Sans", "Gowun Dodum", "Roboto", "Helvetica", "Arial", sans-serif',
                    wordBreak: { xs: 'break-word', md: 'keep-all' },
                    overflowWrap: { xs: 'break-word', md: 'normal' },
                    hyphens: 'none',
                    animation: 'fadeInSentence 0.8s ease-out',
                    '@keyframes fadeInSentence': {
                      from: { opacity: 0, transform: 'scale(0.95)' },
                      to: { opacity: 1, transform: 'scale(1)' }
                    }
                  }}
                >
                  {cleanText(worshipData.sermonSentences[currentSentenceIndex])}
                  {currentSentenceIndex < worshipData.sermonSentences.length - 1 ? '.' : ''}
                </Typography>
              </Box>
            )}
            
            {showFullSermon ? (
              // 모두 보기 모드의 버튼들
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, position: 'relative', zIndex: 1100 }}>
                <Button 
                  variant="outlined"
                  color="secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowFullSermon(false);
                    setCurrentSentenceIndex(worshipData.sermonSentences.length - 1);
                    setSermonCompleted(true);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowFullSermon(false);
                    setCurrentSentenceIndex(worshipData.sermonSentences.length - 1);
                    setSermonCompleted(true);
                  }}
                  sx={{ 
                    borderRadius: 25,
                    minHeight: { xs: '48px', sm: '40px' },
                    fontSize: { xs: '1rem', sm: '0.875rem' },
                    py: { xs: 1.5, sm: 1 }
                  }}
                >
                  {t('worship.sermon.close')}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      const element = document.createElement('a');
                      const content = (worshipData?.sermon as string) || (worshipData?.sermonSentences?.join('.\n\n') as string) || '';
                      const file = new Blob([content], { type: 'text/plain' });
                      element.href = URL.createObjectURL(file);
                      element.download = currentLanguage === 'en' ? 'sermon.txt' : '설교말씀.txt';
                      document.body.appendChild(element);
                      element.click();
                      document.body.removeChild(element);
                    } catch (_) {}
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      const element = document.createElement('a');
                      const content = (worshipData?.sermon as string) || (worshipData?.sermonSentences?.join('.\n\n') as string) || '';
                      const file = new Blob([content], { type: 'text/plain' });
                      element.href = URL.createObjectURL(file);
                      element.download = currentLanguage === 'en' ? 'sermon.txt' : '설교말씀.txt';
                      document.body.appendChild(element);
                      element.click();
                      document.body.removeChild(element);
                    } catch (_) {}
                  }}
                  sx={{ 
                    borderRadius: 25,
                    minHeight: { xs: '48px', sm: '40px' },
                    fontSize: { xs: '1rem', sm: '0.875rem' },
                    py: { xs: 1.5, sm: 1 }
                  }}
                >
                  {currentLanguage === 'en' ? 'Download' : '다운로드'}
                </Button>
              </Box>
            ) : sermonCompleted ? (
              // 설교 완료 후 버튼들
              <Box sx={{ 
                mt: 4, 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <Button 
                  variant="outlined"
                  color="secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentSentenceIndex(0);
                    setSermonCompleted(false);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentSentenceIndex(0);
                    setSermonCompleted(false);
                  }}
                  sx={{ 
                    borderRadius: 25,
                    minHeight: { xs: '48px', sm: '40px' },
                    fontSize: { xs: '1rem', sm: '0.875rem' },
                    py: { xs: 1.5, sm: 1 }
                  }}
                >
                  {t('worship.sermon.replay')}
                </Button>
                <Button 
                  variant="contained"
                  color="secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowFullSermon(true);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowFullSermon(true);
                  }}
                  sx={{ 
                    borderRadius: 25,
                    minHeight: { xs: '48px', sm: '40px' },
                    fontSize: { xs: '1rem', sm: '0.875rem' },
                    py: { xs: 1.5, sm: 1 }
                  }}
                >
                  {t('worship.sermon.viewAll')}
                </Button>
              </Box>
            ) : (
              // 진행 중일 때 문장 카운터만 표시
              <Box sx={{ mt: 4 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  {currentSentenceIndex + 1} / {worshipData.sermonSentences.length}
                </Typography>
              </Box>
            )}
          </Box>
          
          {sermonCompleted && !showFullSermon && (
            <Box
              sx={{
                position: 'fixed',
                bottom: 20,
                right: 20,
                backgroundColor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                borderRadius: 3,
                px: 3,
                py: 2,
                zIndex: 1000,
                opacity: 1, // 즉시 표시하여 깜빡임 완전 방지
                pointerEvents: 'none',
              }}
            >
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium' }}>
                {(() => {
                  if (!isMobile) return t('worship.praiseMessage');
                  const mobileText = t('worship.praiseMessageMobile');
                  return mobileText === 'worship.praiseMessageMobile' ? t('worship.praiseMessage') : mobileText;
                })()}
              </Typography>
            </Box>
          )}
        </Container>
      </BackgroundContainer>
    );
  }

  // 두번째 찬송가 페이지
  if (currentStep === WORSHIP_STEPS.HYMN2) {
    return (
      <BackgroundContainer 
        overlay="rgba(0, 0, 0, 0.3)"
        sx={{ p: 3 }}
      >
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'white', mb: 2 }}>{t('worship.praise')}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'semibold', color: 'white' }}>{worshipData.hymn2.title}</Typography>
          </Box>
          
          <Box sx={{ backgroundColor: 'transparent', border: '2px solid white', borderRadius: 4, p: 4 }}>
            <Box 
              sx={{ 
                aspectRatio: '16/9', 
                maxWidth: '800px', 
                mx: 'auto', 
                borderRadius: 2, 
                overflow: 'hidden',
                boxShadow: 2,
                position: 'relative',
                cursor: secondHymnPlaying ? 'default' : 'pointer'
              }}
              onClick={() => {
                if (!secondHymnPlaying && !secondHymnShowingPlaceholder) {
                  setSecondHymnUserInitiated(true);
                  setSecondHymnShowingPlaceholder(true);
                  setTimeout(() => {
                    setSecondHymnShowingPlaceholder(false);
                    setSecondHymnPlaying(true);
                  }, 2000);
                }
              }}
            >
              {/* YouTube 동영상 - 썸네일이 사라진 후에만 로드 및 재생 */}
              {secondHymnPlaying && (
                <iframe
                  width="100%"
                  height="100%"
                  src={(() => {
                    const embedUrl = convertToEmbedUrl(worshipData.hymn2.videoUrl);
                    const separator = embedUrl.includes('?') ? '&' : '?';
                    return `${embedUrl}${separator}autoplay=0&rel=0&modestbranding=1&controls=1&showinfo=0&iv_load_policy=3&loop=0&disablekb=0&fs=1&enablejsapi=1&playsinline=1`;
                  })()}
                  style={{ 
                    borderRadius: '8px',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: 1,
                    zIndex: 1
                  }}
                  allowFullScreen
                  title="찬송가 비디오"
                  allow="autoplay; encrypted-media; gyroscope; picture-in-picture; clipboard-write; web-share"
                  sandbox="allow-same-origin allow-scripts allow-forms allow-presentation"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              )}
              
              {secondHymnShowingPlaceholder && (
                // Placeholder 이미지 표시 - play button 제거, 3초 후 페이드아웃
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${getImageUrl('youtube_thumbnail.png')})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: '8px',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: secondHymnThumbnailFading ? 0 : 1,
                    transition: 'opacity 1s ease-out',
                    zIndex: 2
                  }}
                />
              )}
              
              {!secondHymnPlaying && !secondHymnShowingPlaceholder && (
                <>
                  {worshipData.hymn2.thumbnail ? (
                    <img 
                      src={worshipData.hymn2.thumbnail}
                      alt={worshipData.hymn2.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        // 썸네일 로드 실패 시 기본 YouTube 썸네일로 교체
                        const img = e.currentTarget as HTMLImageElement;
                        if (!img.src.includes('youtube_thumbnail.png')) {
                          img.src = getImageUrl('youtube_thumbnail.png');
                        } else {
                          // 기본 이미지도 실패 시 gradient 배경으로 교체
                          img.style.display = 'none';
                          img.parentElement!.style.background = 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)';
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%', 
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: 'white', fontSize: '4rem' }}>🎵</Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      width: 80,
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        transform: 'translate(-50%, -50%) scale(1.1)',
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Typography sx={{ color: 'white', fontSize: '2rem' }}>▶</Typography>
                  </Box>
                </>
              )}
            </Box>
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>{t('worship.hymn.channel')}: {worshipData.hymn2.channel}</Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>{t('worship.hymn.duration')}: {worshipData.hymn2.duration}</Typography>
              </Box>
            </Box>
          </Box>
          
          {/* 설교말씀 우하단 버튼 제거됨 */}
        </Container>
      </BackgroundContainer>
    );
  }

  // 축도 페이지 (fade text + 음성 재생 + 배경음악)
  if (currentStep === WORSHIP_STEPS.BLESSING) {
    return (
      <BackgroundContainer overlay="rgba(0, 0, 0, 0.3)"
        sx={{
          overflowX: 'hidden',
          overflowY: isMobile ? 'hidden' : 'auto',
          WebkitOverflowScrolling: 'touch',
          ...(isMobile && {
            height: '100dvh',
            minHeight: '100dvh',
            maxHeight: '100dvh',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            flexDirection: 'column',
          })
        }}
      >
        
        <Container maxWidth="lg" sx={{ 
          position: 'relative', 
          zIndex: 10, 
          textAlign: 'center', 
          px: 3,
          ...(isMobile && {
            flex: '1 1 auto',
            minHeight: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          })
        }}>
          <Box
            sx={{
              position: 'relative',
              // Use dynamic viewport height to handle Android URL bar resizing
              height: { xs: 'auto', md: '80vh' },
              flex: { xs: '1 1 auto', md: 'none' },
              minHeight: 0,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              pt: { xs: 3, md: 4 }
            }}
          >
            {/* 축도 제목 - 상단 고정 */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                flexShrink: 0,
                mb: 3,
                pb: 2,
                // 제목 뒤에 그라데이션 배경을 추가하여 본문이 제목 뒤로 사라지는 효과
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '120%',
                  height: '60px',
                  background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 100%)',
                  pointerEvents: 'none',
                  zIndex: -1
                }
              }}
            >
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: 'white', 
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                  opacity: 1,
                  position: 'relative',
                  zIndex: 1
                }}
              >
                {t('worship.blessingTitle')}
              </Typography>
            </Box>
            
            {/* 축도 화면에서 목사/스타일 정보 UI 제거 */}
            
            {/* 축도 본문 - 단계별 표시 (제목 아래에서 스크롤) */}
            <Box
              ref={blessingContainerRef}
              data-blessing-scroll={isMobile && showBlessingReference ? 'true' : undefined}
              sx={{
                position: 'relative',
                maxWidth: { xs: '100%', sm: '90%', md: '900px' },
                width: '100%',
                // 제목 높이를 고려한 본문 영역 높이
                maxHeight: {
                  xs: showBlessingReference ? 'none' : 'calc(80dvh - 180px)',
                  md: 'calc(80vh - 180px)'
                },
                flex: { xs: showBlessingReference ? '1 1 auto' : 'none', md: 'none' },
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                // 모바일에서 성경구절이 나오면 본문을 그 위 영역에서 스크롤해 끝까지 읽게 한다
                overflow: isMobile && showBlessingReference ? 'auto' : 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: isMobile && showBlessingReference ? 'pan-y' : 'auto',
                pt: 2,
                pb: 4,
                px: { xs: 2, sm: 3, md: 4 }
              }}
            >

              
              {visibleBlessingLines > 0 && (
                <Box
                  sx={{
                    width: '100%',
                    height: isMobile && showBlessingReference ? 'auto' : '100%',
                    position: 'relative',
                    transform: (() => {
                      // 성경구절이 하단에 자리 잡은 뒤에는 본문 전체를 스크롤 영역에 펼친다
                      if (isMobile && showBlessingReference && (blessingPhase === 'complete' || blessingAnimationComplete)) {
                        return 'translateY(0)';
                      }
                      if (blessingPhase === 'scrollUp' || blessingPhase === 'complete') {
                        const currentVisibleLines = visibleBlessingLines;
                        const maxDisplayLines = maxLinesBeforeScroll;
                        if (currentVisibleLines > maxDisplayLines) {
                          const fontSize = window.innerWidth < 768 ? 22.4 : 28.8; // px
                          const lineHeightRatio = 1.4;
                          const marginBottom = window.innerWidth < 768 ? 12.8 : 19.2;
                          const actualLineHeight = fontSize * lineHeightRatio + marginBottom;
                          const linesToScroll = currentVisibleLines - maxDisplayLines;
                          let scrollDistance = linesToScroll * actualLineHeight;
                          // 영어 모드에서 마지막에 본문이 영역 상단으로 사라지지 않도록 중앙에 정지 보정
                          if (currentLanguage === 'en' && blessingPhase === 'complete') {
                            const containerHeight = (blessingContainerRef.current?.clientHeight || 0);
                            const contentHeight = Math.min(currentVisibleLines, maxDisplayLines) * actualLineHeight;
                            const extraCentering = Math.max(0, (containerHeight - contentHeight) / 2);
                            scrollDistance = Math.max(0, scrollDistance - extraCentering);
                          }
                          return `translateY(-${scrollDistance}px)`;
                        }
                      }
                      return 'translateY(0)';
                    })(),
                    transition: (() => {
                      if (isMobile && showBlessingReference && (blessingPhase === 'complete' || blessingAnimationComplete)) {
                        return 'none';
                      }
                      if (blessingVerseComplete) {
                        return 'none'; // 성경 구절 완료 후에는 모든 전환 비활성화
                      } else if (blessingPhase === 'scrollUp') {
                        // 전체 본문이 끊김없이 천천히 위로 스크롤 (3.5초에 맞춰 부드럽게)
                        return `transform 3.5s cubic-bezier(0.23, 1, 0.32, 1)`;
                      } else if (blessingPhase === 'complete') {
                        return 'none'; // 완료 상태에서는 전환 없이 위치 유지
                      }
                      return 'transform 0.8s ease-out';
                    })()
                  }}
                >
                  {blessingLines.slice(0, visibleBlessingLines).map((line, index) => (
                    <Typography 
                      key={index}
                      sx={{ 
                        color: 'white',
                        lineHeight: 1.4, // 줄 높이를 줄여서 더 많은 줄 표시
                        textAlign: 'center',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        fontWeight: 'medium',
                        wordBreak: 'keep-all',
                        overflowWrap: 'normal',
                        hyphens: 'none',
                        whiteSpace: 'pre-wrap',
                        // 줄바꿈 시 단어 한두 개만 남지 않도록 균형 있게 배분
                        textWrap: 'balance',
                        wordWrap: 'normal',
                        fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.8rem' },
                        mb: 0.8, // 행간 간격을 크게 줄임
                        px: { xs: 1, sm: 2 }, // 모바일에서 좌우 패딩 추가
                        opacity: (() => {
                          // 성경 구절 완료 후 또는 애니메이션 완료 후에는 모든 줄이 완전히 표시됨
                          if (blessingVerseComplete || blessingAnimationComplete || blessingPhase === 'complete') {
                            return 1; // 모든 줄 완전히 표시, 더 이상 변경 없음
                          }
                          // 스크롤 단계에서는 모든 줄이 즉시 표시되고, 새로운 줄만 아래에서 페이드인
                          if (blessingPhase === 'scrollUp') {
                            return index === visibleBlessingLines - 1 ? 0 : 1; // 새로운 줄만 투명
                          }
                          // lineByLine 단계에서는 기존 로직 유지
                          return index === visibleBlessingLines - 1 ? 0 : 1;
                        })(),
                        animation: (() => {
                          // 성경 구절 완료 후에는 모든 애니메이션 중지
                          if (blessingVerseComplete || blessingAnimationComplete) {
                            return 'none';
                          }
                          // 완료 단계에서도 애니메이션 중지
                          if (blessingPhase === 'complete') {
                            return 'none';
                          }
                          // 새로 나타나는 줄만 애니메이션 (활성 단계에서만)
                          if (index === visibleBlessingLines - 1 && (blessingPhase === 'lineByLine' || blessingPhase === 'scrollUp')) {
                            if (blessingPhase === 'scrollUp') {
                              // 스크롤 단계에서는 아래에서 올라오는 애니메이션 (전체 스크롤과 동일한 타이밍)
                              return 'slideUpFromBottom 3.5s cubic-bezier(0.23, 1, 0.32, 1) forwards';
                            } else if (blessingPhase === 'lineByLine') {
                              // lineByLine 단계에서는 기존 페이드인 애니메이션
                              return 'fadeInLine 2.5s cubic-bezier(0.23, 1, 0.32, 1) forwards';
                            }
                          }
                          return 'none';
                        })(),
                        // 언어별 추가 설정
                        ...(currentLanguage === 'ja' || currentLanguage === 'zh' || currentLanguage === 'ko' || currentLanguage === 'th' ? {
                          // CJK 언어에서도 단어 단위로만 줄바꿈 유지
                          wordBreak: 'keep-all',
                          lineBreak: 'auto'
                        } : {}),
                        // 애니메이션 완료 후 transform 고정
                        transform: (blessingVerseComplete || blessingAnimationComplete || blessingPhase === 'complete') 
                          ? 'translateY(0) scale(1)' : undefined,
                        '@keyframes fadeInLine': {
                          from: { 
                            opacity: 0, 
                            transform: 'translateY(30px) scale(0.95)' 
                          },
                          to: { 
                            opacity: 1, 
                            transform: 'translateY(0) scale(1)' 
                          }
                        },
                        '@keyframes slideUpFromBottom': {
                          from: { 
                            opacity: 0, 
                            transform: 'translateY(50px)' 
                          },
                          to: { 
                            opacity: 1, 
                            transform: 'translateY(0)' 
                          }
                        }
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
            
            {/* 축도 본문 섹션 완료 - 성경 구절은 별도 섹션으로 분리 */}
          </Box>
          
          {/* 축도 페이지 우하단 버튼 제거됨 */}
        </Container>
        
        {/* 참고 성경말씀 별도 섹션 */}
        {showBlessingReference && (
          <Container 
            maxWidth="lg" 
            sx={{ 
              position: isMobile ? 'relative' : 'absolute',
              bottom: isMobile ? 'auto' : 0,
              left: 0,
              right: 0,
              flexShrink: 0,
              width: '100%',
              pb: isMobile ? 2 : 4,
              px: 3,
              zIndex: 10
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
              }}
            >
              {/* 성경 구절 참조 - 먼저 나타나고 고정 */}
              <Typography 
                variant="h5" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'medium',
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                  mb: 2,
                  fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
                  wordBreak: { xs: 'break-word', md: 'keep-all' },
                  overflowWrap: { xs: 'break-word', md: 'normal' },
                  hyphens: 'none',
                  whiteSpace: { xs: 'normal', md: 'nowrap' },
                  wordWrap: { xs: 'break-word', md: 'normal' },
                  px: { xs: 2, sm: 3 }, // 모바일에서 좌우 패딩 추가
                  opacity: blessingVerseComplete ? 1 : 0,
                  transform: blessingVerseComplete ? 'translateY(0) scale(1)' : undefined,
                  animation: blessingVerseComplete ? 'none' : 'blessingReferenceFadeIn 2.5s cubic-bezier(0.23, 1, 0.32, 1) forwards',
                  '@keyframes blessingReferenceFadeIn': {
                    from: { 
                      opacity: 0, 
                      transform: 'translateY(30px) scale(0.95)' 
                    },
                    to: { 
                      opacity: 1, 
                      transform: 'translateY(0) scale(1)' 
                    }
                  },
                  // 언어별 추가 설정
                  ...(currentLanguage === 'ja' || currentLanguage === 'zh' || currentLanguage === 'ko' || currentLanguage === 'th' ? {
                    wordBreak: 'keep-all',
                    lineBreak: 'strict'
                  } : {})
                }}
              >
                {(() => {
                  const reference = worshipData.blessing?.blessing_reference || t('worship.defaultBlessingReference');
                  return reference;
                })()}
              </Typography>
              
              {/* 성경 구절 본문 - 참조 아래에 별도로 나타남 */}
              {showBlessingVerseText && (
                <Typography 
                  variant="h6"
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'normal',
                    fontStyle: 'italic',
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                    mt: 2,
                    maxWidth: { xs: '100%', sm: '90%', md: '600px' },
                    lineHeight: 1.6,
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                    wordBreak: { xs: 'break-word', md: 'keep-all' },
                    overflowWrap: { xs: 'break-word', md: 'normal' },
                    hyphens: 'none',
                    whiteSpace: 'pre-wrap',
                    wordWrap: { xs: 'break-word', md: 'normal' },
                    px: { xs: 2, sm: 3 }, // 모바일에서 좌우 패딩 추가
                    opacity: blessingVerseComplete ? 1 : 0,
                    transform: blessingVerseComplete ? 'translateY(0) scale(1)' : undefined,
                    animation: blessingVerseComplete ? 'none' : 'blessingVerseTextFadeIn 3s cubic-bezier(0.23, 1, 0.32, 1) forwards',
                    '@keyframes blessingVerseTextFadeIn': {
                      from: { 
                        opacity: 0, 
                        transform: 'translateY(30px) scale(0.95)' 
                      },
                      to: { 
                        opacity: 1, 
                        transform: 'translateY(0) scale(1)' 
                      }
                    },
                    // 언어별 추가 설정
                    ...(currentLanguage === 'ja' || currentLanguage === 'zh' || currentLanguage === 'ko' || currentLanguage === 'th' ? {
                      wordBreak: 'keep-all',
                      lineBreak: 'strict',
                      textWrap: 'balance'
                    } : {}),
                    '& .quote-content': {
                      display: 'inline',
                      wordBreak: 'keep-all',
                      overflowWrap: 'normal',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'normal',
                      // 언어별 추가 설정
                      ...(currentLanguage === 'ja' || currentLanguage === 'zh' || currentLanguage === 'ko' || currentLanguage === 'th' ? {
                        lineBreak: 'strict',
                        textWrap: 'balance'
                      } : {})
                    }
                  }}
                >
                  <span style={{ whiteSpace: 'nowrap' }}>"</span>
                  <span className="quote-content">{(() => {
                    const verse = worshipData.blessing?.blessing_verse || t('worship.defaultBlessingVerse');
                    const cleanedVerse = cleanVerseQuotes(verse);
                    return cleanedVerse;
                  })()}</span>
                  <span style={{ whiteSpace: 'nowrap' }}>"</span>
                </Typography>
              )}

              {/* 축도 동영상 생성 안내 프롬프트 - 참고 성경 말씀 영역 아래쪽에 배치 */}
              {showCreateVideoPrompt && !isRecordingMode && (
                isMobile ? (
                  // 모바일: 동영상 생성/공유 기능을 건너뛰고 안내만 표시
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setShowCreateVideoPrompt(false);
                        setShowChurchInvitationMessage(true);
                        setCurrentStep(WORSHIP_STEPS.CHURCH_INVITATION);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setShowCreateVideoPrompt(false);
                          setShowChurchInvitationMessage(true);
                          setCurrentStep(WORSHIP_STEPS.CHURCH_INVITATION);
                        }
                      }}
                      sx={{
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        px: 2,
                        py: 1,
                        borderRadius: 20,
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      <Typography variant="body1" sx={{ color: 'white' }}>
                        {t('worship.desktopVideoNotice')}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      {currentLanguage === 'en' 
                        ? 'You can save the benediction as a video. Would you like to create it?'
                        : '축도를 동영상으로 만들어서 간직할 수 있습니다. 동영상을 만들까요?'}
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => { setShowRecordConfirmModal(true); }}
                    >
                      {currentLanguage === 'en' ? 'Yes, create video' : '네, 동영상으로 만들기'}
                    </Button>
                    <Button
                      variant="contained"
                      color="inherit"
                      sx={{ backgroundColor: '#FFFFFF', color: '#6F4E37', '&:hover': { backgroundColor: '#F5EDE3' } }}
                      onClick={() => {
                        setShowCreateVideoPrompt(false);
                        setShowChurchInvitationMessage(true);
                        setCurrentStep(WORSHIP_STEPS.CHURCH_INVITATION);
                      }}
                    >
                      {currentLanguage === 'en' ? 'No' : '아니오'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      disabled={isGeneratingServerVideo}
                      onClick={async () => {
                        try {
                          setIsGeneratingServerVideo(true);
                          const blessingText = (typeof worshipData.blessing === 'string')
                            ? worshipData.blessing
                            : (worshipData.blessing?.blessing_verse || worshipData.blessing?.content || '');
                          const apiUrl = getApiUrl('/worship/blessing-video');
                          const res = await fetch(apiUrl, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ blessing_text: blessingText, width: 1080, height: 1920, fps: 30 })
                          });
                          if (!res.ok) throw new Error('failed');
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'blessing.mp4';
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                        } catch (_) {
                          // noop
                        } finally {
                          setIsGeneratingServerVideo(false);
                        }
                      }}
                    >
                      {isGeneratingServerVideo ? (currentLanguage === 'en' ? 'Generating...' : '생성 중...') : (currentLanguage === 'en' ? 'Download server video' : '서버로 영상 다운로드')}
                    </Button>
                  </Box>
                )
              )}
            </Box>
          </Container>
        )}

        {/* 축도 동영상 생성 안내 프롬프트 */}
        {showCreateVideoPrompt && !isRecordingMode && !showBlessingReference && (
          isMobile ? (
            // 모바일: 전체 화면 터치 시 교회 권유 단계로 이동
            <Box
              onClick={() => {
                setShowCreateVideoPrompt(false);
                setShowChurchInvitationMessage(true);
                setCurrentStep(WORSHIP_STEPS.CHURCH_INVITATION);
              }}
              sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                zIndex: 2000,
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              <Box
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  px: 3,
                  py: 2,
                  borderRadius: 24,
                  textAlign: 'center',
                  maxWidth: '90%'
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {t('worship.desktopVideoNotice')}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                position: 'fixed',
                bottom: 24,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                zIndex: 1500
              }}
            >
              <Box
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  px: 3,
                  py: 2,
                  borderRadius: 24,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {currentLanguage === 'en' 
                    ? 'You can save the benediction as a video. Would you like to create it?'
                    : '축도를 동영상으로 만들어서 간직할 수 있습니다. 동영상을 만들까요?'}
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => { setShowRecordConfirmModal(true); }}
                >
                  {currentLanguage === 'en' ? 'Yes, create video' : '네, 동영상으로 만들기'}
                </Button>
                <Button
                  variant="contained"
                  color="inherit"
                  sx={{ backgroundColor: '#FFFFFF', color: '#6F4E37', '&:hover': { backgroundColor: '#F5EDE3' } }}
                  onClick={() => {
                    setShowCreateVideoPrompt(false);
                    setShowChurchInvitationMessage(true);
                    setCurrentStep(WORSHIP_STEPS.CHURCH_INVITATION);
                  }}
                >
                  {currentLanguage === 'en' ? 'No' : '아니오'}
                </Button>
              </Box>
            </Box>
          )
        )}

        {/* 엔드 카드 오버레이: hands.png + 문구 */}
        {showEndCard && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.75)'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src={getImageUrl('hands.png')}
                alt="기도하는 손"
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mb: 2, 
                  opacity: 0.95,
                  filter: 'brightness(1.6) contrast(1.1) saturate(0.9)'
                }}
              />
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', fontSize: '2em', lineHeight: 1.2 }}>
                He Listens to Me
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'white', opacity: 0.9, fontSize: '2em', lineHeight: 1.2 }}>
                www.HeListens.Me
              </Typography>
            </Box>
          </Box>
        )}
        
        {/* 동영상 생성 시작 확인 모달 */}
        <Dialog
          open={showRecordConfirmModal}
          onClose={(_event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            setShowRecordConfirmModal(false);
          }}
          keepMounted
          transitionDuration={{ appear: 150, enter: 150, exit: 120 }}
          PaperProps={{
            sx: { borderRadius: 4, p: 2, maxWidth: '480px', mx: 2 }
          }}
          sx={{ zIndex: 9999 }}
        >
          <DialogTitle sx={{ fontWeight: 'bold' }}>
            {currentLanguage === 'en' ? 'Prepare to capture video' : '동영상 캡처 준비'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
              {currentLanguage === 'en'
                ? 'We will adjust the browser window to 1280x720 and start capturing. Do not close or click the screen until the preview appears.'
                : '브라우저 화면 크기는 1280 x 720 픽셀 이하를 권장합니다. 화면이 너무 크면 동영상 생성에 실패할 수 있습니다.\n동영상 미리보기 창이 나타날 때까지 화면을 닫거나 클릭하지 마세요.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRecordConfirmModal(false)}>
              {currentLanguage === 'en' ? 'Cancel' : '취소'}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setShowRecordConfirmModal(false);
                // 1280x720으로 리사이즈 시도 후 녹화 흐름 시작
                attemptResizeWindow(1280, 720);
                beginRecordingFlow();
              }}
            >
              {currentLanguage === 'en' ? 'OK' : '확인'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 축도 중단 확인 모달 */}
        <Dialog
          open={showBlessingStopModal}
          // backdrop/escape로는 닫히지 않도록 하여 깜빡임 방지
          onClose={(_event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            setShowBlessingStopModal(false);
          }}
          keepMounted
          transitionDuration={{ appear: 150, enter: 150, exit: 120 }}
          PaperProps={{
            sx: {
              borderRadius: 4,
              p: 2,
              maxWidth: '400px',
              mx: 2,
              zIndex: 9999  // 모바일에서 다른 요소들보다 위에 표시되도록 설정
            }
          }}
          sx={{
            zIndex: 9999  // Dialog 자체의 z-index도 높게 설정
          }}
        >
          <DialogTitle 
            sx={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              color: 'primary.main',
              pb: 1
            }}
          >
            {t('worship.blessing.stopConfirm.title') || '축도를 멈추시겠습니까?'}
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <Typography variant="body1" color="text.secondary">
              {t('worship.blessing.stopConfirm.message') || '축도가 진행 중입니다. 정말로 멈추고 다음 단계로 넘어가시겠습니까?'}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 1, pt: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setShowBlessingStopModal(false)}
              sx={{ 
                borderRadius: 25,
                minWidth: '80px',
                color: 'text.secondary',
                borderColor: 'grey.300',
                touchAction: 'manipulation',  // 모바일 터치 최적화
                WebkitTouchCallout: 'none',   // iOS 터치 호출 비활성화
                WebkitTapHighlightColor: 'transparent'  // 터치 하이라이트 제거
              }}
            >
              {t('worship.blessing.stopConfirm.cancel') || '계속'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                setShowBlessingStopModal(false);
                // 녹화 중이면 즉시 녹화 중단 후 리뷰 페이지로 이동
                if (isRecordingMode && isRecording) {
                  let videoUrl: string | null = null;
                  try {
                    const result = await stopRecording();
                    videoUrl = result?.url || null;
                  } catch (_) { /* ignore */ }
                  navigate('/worship-review', {
                    state: {
                      videoUrl,
                      genderType: location.state?.genderType || 'brother',
                      worshipData,
                      prayerId: location.state?.prayerId,
                    }
                  });
                  return;
                }
                // 녹화 중이 아니면 기존 로직대로 다음 단계로 이동
                if (currentStep < WORSHIP_STEPS.DONATION) {
                  setCurrentStep(currentStep + 1);
                }
              }}
              sx={{ 
                borderRadius: 25,
                minWidth: '80px',
                touchAction: 'manipulation',  // 모바일 터치 최적화
                WebkitTouchCallout: 'none',   // iOS 터치 호출 비활성화
                WebkitTapHighlightColor: 'transparent'  // 터치 하이라이트 제거
              }}
            >
              {t('worship.blessing.stopConfirm.confirm') || '멈추기'}
            </Button>
          </DialogActions>
        </Dialog>
      </BackgroundContainer>
    );
  }

  // 교회 권유 페이지
  if (currentStep === WORSHIP_STEPS.CHURCH_INVITATION) {
    return (
      <BackgroundContainer 
        overlay="rgba(0, 0, 0, 0.3)"
        sx={{ overflow: 'hidden' }}
      >
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10, textAlign: 'center', px: 3 }}>
          {showChurchInvitationMessage && (
            <Typography 
              sx={{ 
                color: 'white',
                fontWeight: 'bold',
                fontSize: { xs: '2.2rem', md: '3rem' },
                wordBreak: 'keep-all',
                overflowWrap: 'normal',
                hyphens: 'none',
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                lineHeight: 1.4,
                animation: 'fadeInTitle 1s ease-out',
                whiteSpace: 'pre-line',
                '@keyframes fadeInTitle': {
                  from: { opacity: 0, transform: 'translateY(-30px)' },
                  to: { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              {t('worship.churchInvitation.message')}
            </Typography>
          )}
        </Container>
      </BackgroundContainer>
    );
  }

  // 헌금 페이지 (부담스럽지 않은 형태)
  if (currentStep === WORSHIP_STEPS.DONATION) {
    return (
      <BackgroundContainer 
        sx={{ 
          p: 3,
          overflow: 'auto',
          minHeight: '100vh'
        }}
      >
        
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 10 }}>
          <Box 
            sx={{ 
              backgroundColor: 'transparent', 
              border: '2px solid white',
              borderRadius: 4, 
              p: { xs: 4, md: 6 }, 
              textAlign: 'center', 
              animation: 'fadeInCard 1.5s ease-out',
              '@keyframes fadeInCard': {
                from: { opacity: 0, transform: 'translateY(30px) scale(0.95)' },
                to: { opacity: 1, transform: 'translateY(0) scale(1)' }
              }
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#bfb5a6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                position: 'relative',
                overflow: 'visible',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '5px',
                  right: '35px',
                  width: '35px',
                  height: '35px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0.1) 70%, transparent 100%)',
                  zIndex: 3,
                  animation: 'sparkle3 4s ease-in-out infinite 2.6s',
                  boxShadow: '0 0 30px 15px rgba(255,255,255,0.2), 0 0 45px 20px rgba(255,255,255,0.1), 0 0 60px 25px rgba(255,255,255,0.05)',
                  filter: 'blur(2px)',
                },
                '@keyframes sparkle3': {
                  '0%, 80%, 100%': { 
                    opacity: 0,
                    transform: 'scale(0.3)',
                  },
                  '40%': { 
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                },
              }}
            >
              <Box
                component="img"
                src={getImageUrl('offering_box.png')}
                alt="헌금함"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  filter: 'sepia(20%) saturate(80%) brightness(1.1) drop-shadow(2px 4px 8px rgba(0, 0, 0, 0.3)) drop-shadow(1px 2px 4px rgba(0, 0, 0, 0.2))',
                  zIndex: 1,
                  position: 'relative',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                }}
              />
            </Box>
            
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white', mb: 3 }}>
              {t('worship.thanksGiving')}
            </Typography>
            
            <Typography variant="h6" sx={{ color: 'white', lineHeight: 1.6, mb: 4, whiteSpace: 'pre-line' }}>
              {t('worship.thanksGivingMessage')}
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 4, color: 'rgba(255, 255, 255, 0.85)' }}>
              {t('worship.donationNotice')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                size="large"
                color="warning"
                onClick={() => navigate('/donation')}
                sx={{ borderRadius: 25, py: 1.5 }}
              >
                {t('worship.donateButton')}
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/')}
                sx={{ borderRadius: 25, py: 1.5 }}
              >
                {t('worship.newWorshipButton')}
              </Button>
            </Box>
            
            {/* 다운로드 버튼들 */}
            <Box sx={{ borderTop: '1px solid', borderColor: 'rgba(255, 255, 255, 0.4)', pt: 3 }}>
              <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.85)' }}>
                {t('worship.downloadResources')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    const element = document.createElement('a');
                    const file = new Blob([worshipData.sermon], { type: 'text/plain' });
                    element.href = URL.createObjectURL(file);
                    element.download = '설교말씀.txt';
                    document.body.appendChild(element);
                    element.click();
                    document.body.removeChild(element);
                  }}
                  sx={{ borderRadius: 20 }}
                >
                  {t('worship.sermonDownload')}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    const element = document.createElement('a');
                    const blessingContent = typeof worshipData.blessing === 'string' 
                      ? worshipData.blessing 
                      : worshipData.blessing?.content || '축도 내용';
                    const rawVerse = worshipData.blessing?.blessing_verse || '';
                    const cleanedVerse = cleanVerseQuotes(rawVerse);
                    const file = new Blob([`${blessingContent}\n\n${worshipData.blessing?.blessing_reference || worshipData.blessingReference}\n"${cleanedVerse}"`], { type: 'text/plain' });
                    element.href = URL.createObjectURL(file);
                    element.download = '축도.txt';
                    document.body.appendChild(element);
                    element.click();
                    document.body.removeChild(element);
                  }}
                  sx={{ borderRadius: 20 }}
                >
                  {t('worship.blessingDownload')}
                </Button>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                borderRadius: 25,
                px: 3,
                py: 1,
                display: 'inline-block'
              }}
            >
              {t('worship.closingMessage')}
            </Typography>
          </Box>
        </Container>
      </BackgroundContainer>
    );
  }

  // 페이지 전환 효과 제거됨 - 깜빡임 방지

  return null;
};

export default WorshipPage; 